const FV_ICON_DIR = svgIcon("folder");
const FV_ICON_FILE = svgIcon("file");
const FV_ICON_ARCHIVE = svgIcon("archive");
// ============================================================================
// File viewer modal (tabbed) + Git-aware diff tree
// Extracted from main.js — depends on DOM elements (dom.js), escapeHtml
// (markdown.js), persistence helpers (persistence.js), and the haptic/Token
// globals from shared.js.
// ============================================================================
// Depends on: haptic (shared.js global), Token (shared.js global),
// escapeHtml (markdown.js), DOM elements (dom.js),
// loadFvState/loadFvStateMap/FV_STATE_KEY (persistence.js).
// marked, hljs, mammoth, XLSX are CDN globals used by file renderers.

import {
  sbFilesBtn,
  fvOverlay, fvClose, fvBreadcrumb, fvPathInput, fvUp, fvHome, fvGo,
  fvNewFile, fvUpload, fvUploadInput, fvSortKey, fvSortDir, fvFilter, fvHidden,
  fvList, fvTabs,
  fvPaneFiles, fvDiffPane, fvDiffBase, fvDiffModeSelect, fvDiffSort,
  fvDiffFilter, fvDiffRefresh, fvDiffSummary, fvDiffTree,
  fvPickRecents, fvPickFoot, fvPickSelected, fvPickConfirm, fvPickCancel,
} from "./dom.js";
import { escapeHtml } from "./markdown.js";
import {
  FV_STATE_KEY, loadFvStateMap, loadFvState, loadRecentDirs,
} from "./persistence.js";
import { icon as svgIcon, fileIcon as svgFileIcon } from "./icons.js";
import { copyText, flashCopied } from "./copy.js";

// ---- deps set by initFileViewer ----
let activeCwdRef = () => "";
let closeSidebarRef = () => {};
// Pick-intent deps (was dir-picker.js): start/move session, recents
let selectedDirRef = () => "";
let setSelectedDirRef = () => {};
let startSessionRef = () => {};
let changeFolderRef = () => {};
let liveCwdsRef = () => new Set();
let removeRecentDirRef = () => {};
// Session/pin deps
let forSessionKeyRef = () => null; // () => sessionId | "pending" | null
let pinOpsRef = null;              // { toggleFilePin, toggleFilePinGlobal, filePinTier }

// ---- file viewer state persistence (per workspace) ----
// saveFvState remains here because it references fvCurrentPath, fvOpenTabs, fvActiveTab.
// clearFvState is imported from persistence.js.
// ---- intent: "browse" (file manager) vs "pick" (choose a session cwd) ----
// The same browser serves both surfaces: pick adds a recents row + a
// Start/Move footer and skips per-workspace state restore.
let fvIntent = "browse";
let fvPickMode = "onboard";      // "onboard" → Start here, "change" → Move here
let fvPickState = "ready";       // "ready" | "create" (ENOENT) | "error"
let fvPickError = "";

/** Session key for pin attribution — resolved live at action time (not
 *  cached at fv-open) so pins always land on the currently active session.
 *  Returns sessionId | "pending" | null (null → global tier). */
const fvSessionKey = () => forSessionKeyRef();

const FV_STATE_KEY_LOCAL = FV_STATE_KEY; // re-exported for any internal references
function saveFvState(cwd) {
  if (!cwd || !fvCurrentPath || fvIntent === "pick") return; // don't clobber saved state before the viewer is initialized / while picking
  const openFiles = [];
  for (const [id, t] of fvOpenTabs) {
    if (id === "files") continue;
    if (t.filePath) openFiles.push({ filePath: t.filePath, fileName: t.fileName });
  }
  const activeTab = fvOpenTabs.get(fvActiveTab);
  const activeFilePath = activeTab && activeTab.filePath ? activeTab.filePath : null;
  const state = { currentPath: fvCurrentPath, openFiles, activeFilePath, ts: Date.now() };
  const map = loadFvStateMap();
  map[cwd] = state;
  try { localStorage.setItem(FV_STATE_KEY, JSON.stringify(map)); }
  catch { /* quota — ignore */ }
}
// (clearFvState is imported from persistence.js)

// Wire up file-link anchors produced by inlineMd so clicking opens the file
// in the built-in file viewer instead of navigating away.
function wireFileLinks(container) {
  if (!container) return;
  container.querySelectorAll("a.file-link[data-filepath]").forEach((a) => {
    if (a.dataset.wired) return;
    a.dataset.wired = "1";
    a.href = "#";
    const lines = a.dataset.lines;
    if (lines) a.title = "Open " + a.textContent + " in file viewer (lines " + lines + ")";
    a.addEventListener("click", (e) => {
      e.preventDefault();
      haptic("tap");
      // Paths are percent-encoded (by expandRefTags and by the CLI's live
      // conversion) — decode before use, else /api/file gets a
      // double-encoded path and fails to resolve.
      let filePath = a.dataset.filepath;
      try { filePath = decodeURIComponent(filePath); } catch { /* keep raw */ }
      const fileName = filePath.split("/").pop() || filePath;
      // data-lines is "a-b" (see expandRefTags) — scroll to that range.
      let initialLine = null, endLine = null;
      if (lines) {
        const [la, lb] = lines.split("-").map((n) => parseInt(n, 10));
        if (la > 0) { initialLine = la; endLine = lb > 0 ? lb : la; }
      }
      openFileInViewer(filePath, fileName, { initialLine, endLine });
    });
  });
}

// ============================================================================
// File viewer modal (tabbed)
// ============================================================================
let fvCurrentPath = "";
let fvHomeDir = "";
let fvActiveTab = "files";
const fvOpenTabs = new Map(); // tabId -> { tabEl, paneEl, fileName, filePath }

const TEXT_EXTS = new Set(["ts","tsx","js","jsx","mjs","cjs","json","md","txt","css","html","htm","py","rb","go","rs","java","c","cpp","h","hpp","cs","php","swift","kt","scala","sh","bash","zsh","fish","yml","yaml","toml","ini","cfg","conf","xml","svg","vue","svelte","sql","graphql","gql","env","gitignore","dockerfile","makefile","lua","r","dart","ex","exs","erl","clj","cljs","edn","nim","v","zig","wasm","asm","s","lock","log","diff","patch"]);

// Binary formats that the file viewer can render (PDF via <iframe>, DOCX via mammoth.js,
// XLSX via SheetJS, PPTX via pptx-preview). CSV is text but rendered as a table.
const BINARY_VIEWABLE_EXTS = new Set(["pdf", "docx", "xlsx", "csv", "pptx"]);

// Media formats rendered via /api/raw blob URLs.
const IMAGE_EXTS = new Set(["png", "jpg", "jpeg", "gif", "webp", "avif", "bmp", "ico"]);
const AUDIO_EXTS = new Set(["mp3", "wav", "ogg", "m4a", "flac", "aac"]);
const VIDEO_EXTS = new Set(["mp4", "webm", "mov", "mkv", "m4v"]);

function mediaKind(ext) {
  if (IMAGE_EXTS.has(ext)) return "img";
  if (AUDIO_EXTS.has(ext)) return "audio";
  if (VIDEO_EXTS.has(ext)) return "video";
  return null;
}

function formatSize(bytes) {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}
function formatTimeAgo(mtimeMs) {
  if (!mtimeMs) return "";
  const sec = Math.floor((Date.now() - mtimeMs) / 1000);
  if (sec < 0) return "just now";
  if (sec < 60) return sec + "s ago";
  const min = Math.floor(sec / 60);
  if (min < 60) return min + "m ago";
  const hr = Math.floor(min / 60);
  if (hr < 24) return hr + "h ago";
  const day = Math.floor(hr / 24);
  if (day < 7) return day + "d ago";
  const wk = Math.floor(day / 7);
  if (wk < 5) return wk + "w ago";
  const mon = Math.floor(day / 30);
  if (mon < 12) return mon + "mo ago";
  return Math.floor(day / 365) + "y ago";
}

function ensureFilesTab() {
  if (fvOpenTabs.has("files")) return;
  const tab = document.createElement("div");
  tab.className = "fv-tab";
  tab.dataset.tabId = "files";
  tab.innerHTML = '<span class="fv-tab-label">' + svgIcon("folder", 14) + ' Files</span>';
  tab.addEventListener("click", () => {
    haptic("tap");
    // Clicking the Files tab while already on it toggles between files and
    // diff view for the current folder. Clicking it from another tab switches
    // to it (in whatever mode it was last in).
    if (fvActiveTab === "files") toggleFvFolderMode();
    else switchFvTab("files");
  });
  fvTabs.appendChild(tab);
  fvOpenTabs.set("files", { tabEl: tab, paneEl: fvPaneFiles, fileName: null, filePath: null });
}

// Folder view toggle: clicking the active Files tab flips the current folder
// between its file listing and a diff tree scoped to that folder. No separate
// Diff tab — the one Files tab does double duty.
let fvDiffLoaded = false;
let fvDiffLastKey = "";
let fvFolderMode = "files"; // "files" | "diff"

function setFvFolderMode(mode) {
  fvFolderMode = mode;
  const on = mode === "diff";
  fvDiffPane.classList.toggle("hidden", !on);
  fvList.classList.toggle("hidden", on);
  // Update the tab label to reflect the current mode.
  const t = fvOpenTabs.get("files");
  if (t) t.tabEl.querySelector(".fv-tab-label").innerHTML = on ? svgIcon("diff", 14) + " Diff" : svgIcon("folder", 14) + " Files";
  // Always go through loadFvDiff — it has its own cache check keyed on
  // (cwd|base|mode), so it re-renders from cache if nothing changed, or
  // re-fetches when the repo / base / mode changed (e.g. switching repos).
  if (on) {
    loadFvBranches().then(() => loadFvDiff());
  }
}

function toggleFvFolderMode() {
  setFvFolderMode(fvFolderMode === "files" ? "diff" : "files");
}

// ----------------------------------------------------------------------------
// Multi-select state, clipboard, and sort for the Files tab.
// ----------------------------------------------------------------------------
let fvSelectMode = false;
let fvSelected = new Set();        // absolute entry paths currently checked
let fvAnchorPath = null;           // last-clicked row — shift+click range anchor
let fvRenderedEntries = [];        // entries as currently rendered (filter+sort applied)
let fvClipboard = null;            // { op: "cut" | "copy", paths: string[] } | null
let fvSort = loadFvSort();         // { key, dir } persisted to localStorage

const FV_SORT_KEYS = ["name", "modified", "size", "type"];

function loadFvSort() {
  try {
    const raw = JSON.parse(localStorage.getItem("fvSort") || "");
    if (raw && FV_SORT_KEYS.includes(raw.key) && (raw.dir === "asc" || raw.dir === "desc")) {
      return { key: raw.key, dir: raw.dir };
    }
  } catch { /* ignore */ }
  return { key: "name", dir: "asc" };
}

function saveFvSort() {
  try { localStorage.setItem("fvSort", JSON.stringify(fvSort)); } catch { /* ignore */ }
}

// Cached directory listing — the source renderFvList() filters/sorts from.
let fvEntries = [];
let fvParentPath = null;
let fvShowHidden = false;
try { fvShowHidden = localStorage.getItem("fvShowHidden") === "1"; } catch { /* ignore */ }

/** Small transient toast (avoids alert() for lightweight confirmations). */
function fvToast(msg) {
  const t = document.createElement("div");
  t.className = "fv-toast";
  t.textContent = msg;
  document.body.appendChild(t);
  requestAnimationFrame(() => t.classList.add("show"));
  setTimeout(() => {
    t.classList.remove("show");
    setTimeout(() => t.remove(), 250);
  }, 1600);
}

/** Copy an absolute path to the clipboard, with a textarea fallback. */
async function copyFvPath(path) {
  try {
    await navigator.clipboard.writeText(path);
  } catch {
    const ta = document.createElement("textarea");
    ta.value = path;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand("copy"); } catch { /* ignore */ }
    ta.remove();
  }
  fvToast("Path copied");
}

/**
 * Retarget open tabs after a rename/move. A renamed file's own tab is
 * repointed at the new path (label + name updated); a renamed folder
 * prefix-rewrites the filePath of every tab beneath it.
 */
function retargetOpenTabs(fromPath, toPath, newName, isDir) {
  for (const [id, t] of fvOpenTabs) {
    if (id === "files" || !t.filePath) continue;
    if (t.filePath === fromPath) {
      t.filePath = toPath;
      t.fileName = newName;
      const label = t.tabEl.querySelector(".fv-tab-label");
      if (label) label.innerHTML = (t.icon || FV_ICON_FILE) + " " + newName.replace(/&/g,"&amp;").replace(/</g,"&lt;");
      const fn = t.paneEl.querySelector(".fv-filename");
      if (fn) fn.textContent = newName;
      const ni = t.paneEl.querySelector(".fv-name-input");
      if (ni) ni.value = newName;
    } else if (isDir && (t.filePath.startsWith(fromPath + "/") || t.filePath.startsWith(fromPath + "\\"))) {
      t.filePath = toPath + t.filePath.slice(fromPath.length);
    }
  }
}

/** Prompt for a new name and rename a file or folder in place via /api/move. */
async function renameFvEntry(entryPath, name, isDir) {
  const dir = entryPath.slice(0, entryPath.length - name.length); // keeps trailing sep
  const newName = await customPrompt({
    title: "Rename " + (isDir ? "folder" : "file"),
    label: "New name",
    defaultValue: name,
    confirmText: "Rename",
    validate: (v) => {
      const t = v.trim();
      if (!t) return "Name is required.";
      if (/[\\/]/.test(t) || t === "." || t === "..") return "Invalid name.";
      if (t === name) return "Name is unchanged.";
      return null;
    },
  });
  if (!newName) return;
  const to = dir + newName;
  try {
    const res = await fetch("/api/move", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": "Bearer " + Token.get() },
      body: JSON.stringify({ from: entryPath, to }),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.error || res.status);
    }
    retargetOpenTabs(entryPath, to, newName, isDir);
    await loadFvDir(fvCurrentPath);
  } catch (e) {
    alert("Rename failed: " + (e.message || e));
  }
}

/** Comparator for the directory listing. Folders always sort first. */
function fvSortCmp(a, b) {
  if (a.isDir !== b.isDir) return a.isDir ? -1 : 1;
  let r = 0;
  switch (fvSort.key) {
    case "modified":
      r = (b.mtime || 0) - (a.mtime || 0); // newer first when asc
      break;
    case "size":
      r = (a.size || 0) - (b.size || 0);
      break;
    case "type": {
      const ea = (a.name.split(".").pop() || "").toLowerCase();
      const eb = (b.name.split(".").pop() || "").toLowerCase();
      r = ea.localeCompare(eb, undefined, { numeric: true });
      if (r === 0) r = a.name.localeCompare(b.name, undefined, { numeric: true });
      break;
    }
    default: // name
      r = a.name.localeCompare(b.name, undefined, { numeric: true });
  }
  return fvSort.dir === "asc" ? r : -r;
}

/** Enter multi-select mode with one row pre-selected. Idempotent if already on. */
function enterFvSelectMode(initialPath) {
  fvSelectMode = true;
  if (!fvSelected.size) fvSelected = new Set([initialPath]);
  else fvSelected.add(initialPath);
  fvList.classList.add("fv-list-selecting");
  renderFvActionbar();
  // Re-render the current listing so checkboxes appear and selection is shown.
  // We rebuild from the last-fetched entries without a network round-trip.
  applyFvSelectModeToRows();
}

/** Toggle a row's membership in the selection; exit select mode when empty. */
function toggleFvRow(path) {
  if (fvSelected.has(path)) fvSelected.delete(path);
  else fvSelected.add(path);
  applyFvSelectModeToRows();
  updateFvActionbarCount();
  if (fvSelected.size === 0) exitFvSelectMode();
}

/** Select every row in fvRenderedEntries (Ctrl/Cmd+A). */
function selectAllFvRows() {
  if (!fvRenderedEntries.length) return;
  fvSelectMode = true;
  for (const e of fvRenderedEntries) fvSelected.add(fvCurrentPath + "/" + e.name);
  fvList.classList.add("fv-list-selecting");
  renderFvActionbar();
  applyFvSelectModeToRows();
  updateFvActionbarCount();
}

/**
 * Shift+click: select the contiguous range between the last-clicked anchor
 * row and clickedIdx (inclusive) in the current render order. Falls back to
 * a single-row selection when no anchor exists.
 */
function selectFvRange(clickedIdx) {
  const pathAt = (i) => fvCurrentPath + "/" + fvRenderedEntries[i].name;
  const anchorIdx = fvAnchorPath ? fvRenderedEntries.findIndex((e) => fvCurrentPath + "/" + e.name === fvAnchorPath) : -1;
  const a = anchorIdx >= 0 ? anchorIdx : clickedIdx;
  fvSelectMode = true;
  for (let i = Math.min(a, clickedIdx); i <= Math.max(a, clickedIdx); i++) {
    fvSelected.add(pathAt(i));
  }
  fvList.classList.add("fv-list-selecting");
  renderFvActionbar();
  applyFvSelectModeToRows();
  updateFvActionbarCount();
}

/** Leave select mode. The clipboard is preserved so paste-elsewhere still works. */
function exitFvSelectMode() {
  fvSelectMode = false;
  fvSelected.clear();
  fvList.classList.remove("fv-list-selecting");
  removeFvActionbar();
  applyFvSelectModeToRows();
}

/** Apply/remove the .fv-item-selected class on each row based on fvSelected. */
function applyFvSelectModeToRows() {
  for (const row of fvList.querySelectorAll(".fv-item")) {
    const path = row.dataset.path;
    if (!path) continue;
    row.classList.toggle("fv-item-selected", fvSelected.has(path));
  }
}

/** Build (or refresh) the bottom action bar in the Files pane. */
function renderFvActionbar() {
  removeFvActionbar();
  const bar = document.createElement("div");
  bar.className = "fv-actionbar";
  bar.id = "fv-actionbar";

  const cancel = document.createElement("button");
  cancel.innerHTML = svgIcon("close", 14) + '<span class="fv-actionbar-label">Cancel</span>';
  cancel.addEventListener("click", () => { haptic("tap"); exitFvSelectMode(); });
  bar.appendChild(cancel);

  const count = document.createElement("span");
  count.className = "fv-actionbar-count";
  bar.appendChild(count);

  const dl = document.createElement("button");
  dl.innerHTML = svgIcon("download", 14) + '<span class="fv-actionbar-label">Download</span>';
  dl.addEventListener("click", () => { haptic("tap"); downloadFvSelection(); });
  bar.appendChild(dl);

  const cut = document.createElement("button");
  cut.innerHTML = svgIcon("cut", 14) + '<span class="fv-actionbar-label">Cut</span>';
  cut.addEventListener("click", () => { haptic("tap"); cutFvSelection(); });
  bar.appendChild(cut);

  const copy = document.createElement("button");
  copy.innerHTML = svgIcon("copy", 14) + '<span class="fv-actionbar-label">Copy</span>';
  copy.addEventListener("click", () => { haptic("tap"); copyFvSelection(); });
  bar.appendChild(copy);

  const paste = document.createElement("button");
  paste.innerHTML = svgIcon("clipboard", 14) + '<span class="fv-actionbar-label">Paste</span>';
  paste.addEventListener("click", () => { haptic("tap"); pasteFvClipboard(); });
  bar.appendChild(paste);

  const del = document.createElement("button");
  del.className = "fv-actionbar-danger";
  del.innerHTML = svgIcon("trash", 14) + '<span class="fv-actionbar-label">Delete</span>';
  del.addEventListener("click", () => { haptic("tap"); deleteFvSelection(); });
  bar.appendChild(del);

  fvPaneFiles.appendChild(bar);
  updateFvActionbarCount();
}

function removeFvActionbar() {
  const existing = document.getElementById("fv-actionbar");
  if (existing) existing.remove();
}

function updateFvActionbarCount() {
  const bar = document.getElementById("fv-actionbar");
  if (!bar) return;
  const n = fvSelected.size;
  bar.querySelector(".fv-actionbar-count").textContent = n + " selected";
  const [cancel, , dl, cut, copy, paste, del] = bar.querySelectorAll("button");
  dl.disabled = n === 0;
  cut.disabled = n === 0;
  copy.disabled = n === 0;
  del.disabled = n === 0;
  paste.disabled = !fvClipboard;
}

/** Stash the current selection as a cut clipboard and exit select mode. */
function cutFvSelection() {
  if (!fvSelected.size) return;
  fvClipboard = { op: "cut", paths: [...fvSelected] };
  exitFvSelectMode();
  alert("Cut " + fvClipboard.paths.length + " item(s). Navigate to a folder and Paste.");
}

/** Stash the current selection as a copy clipboard and exit select mode. */
function copyFvSelection() {
  if (!fvSelected.size) return;
  fvClipboard = { op: "copy", paths: [...fvSelected] };
  exitFvSelectMode();
  alert("Copied " + fvClipboard.paths.length + " item(s). Navigate to a folder and Paste.");
}

/** Move or copy the clipboard contents into the current directory. */
async function pasteFvClipboard() {
  if (!fvClipboard || !fvClipboard.paths.length) { alert("Nothing to paste"); return; }
  if (!fvCurrentPath) return;
  const sep = fvCurrentPath.includes("\\") ? "\\" : "/";
  const dir = fvCurrentPath.replace(/[\\/]+$/, "");
  let ok = 0, failed = [];
  for (const src of fvClipboard.paths) {
    const base = src.split(/[\\/]/).pop();
    let dst = dir + sep + base;
    // Same-directory copy guard: auto-suffix " (copy)" to avoid a self-copy no-op.
    if (fvClipboard.op === "copy" && src.replace(/[\\/]+$/, "") === dir) {
      dst = dir + sep + base.replace(/(\.[^.]+)?$/, (m) => " (copy)" + m);
    }
    if (fvClipboard.op === "cut" && src.replace(/[\\/]+$/, "") === dir) {
      // Cut into the same dir is a no-op.
      continue;
    }
    try {
      const res = await fetch("/api/" + (fvClipboard.op === "cut" ? "move" : "copy"), {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + Token.get() },
        body: JSON.stringify({ from: src, to: dst }),
      });
      if (res.status === 409) {
        // Destination exists (move only). Confirm overwrite.
        const yes = await customConfirm({
          title: "Overwrite?",
          message: '"' + base + '" already exists here. Overwrite it?',
          confirmText: "Overwrite",
          destructive: true,
        });
        if (!yes) { failed.push(base + " (skipped)"); continue; }
        // Remove the existing destination, then retry the move.
        await fetch("/api/file?path=" + encodeURIComponent(dst), {
          method: "DELETE",
          headers: { "Authorization": "Bearer " + Token.get() },
        });
        const retry = await fetch("/api/move", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": "Bearer " + Token.get() },
          body: JSON.stringify({ from: src, to: dst }),
        });
        if (!retry.ok) {
          const e = await retry.json().catch(() => ({}));
          failed.push(base + " (" + (e.error || retry.status) + ")");
          continue;
        }
      } else if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        failed.push(base + " (" + (e.error || res.status) + ")");
        continue;
      }
      ok++;
      // If a moved file was open in a tab, close it (matches deleteFvEntry behavior).
      if (fvClipboard.op === "cut") {
        for (const [tabId, t] of fvOpenTabs) {
          if (tabId === "files") continue;
          if (t.filePath === src) closeFvTab(tabId);
        }
      }
    } catch (e) {
      failed.push(base + " (" + (e.message || e) + ")");
    }
  }
  if (fvClipboard.op === "cut") fvClipboard = null;
  updateFvActionbarCount();
  await loadFvDir(fvCurrentPath);
  if (failed.length) alert("Pasted " + ok + ", failed " + failed.length + ":\n\n" + failed.join("\n"));
  else if (ok) alert("Pasted " + ok + " item(s).");
}

/** Download the current selection: single item reuses the existing path, many use /api/download-multi. */
async function downloadFvSelection() {
  const paths = [...fvSelected];
  if (!paths.length) return;
  if (paths.length === 1) {
    const p = paths[0];
    const name = p.split(/[\\/]/).pop();
    const isDir = !!document.querySelector('.fv-item[data-path="' + CSS.escape(p) + '"][data-isdir="1"]');
    downloadFvEntry(p, name, isDir);
    return;
  }
  try {
    const res = await fetch("/api/download-multi", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": "Bearer " + Token.get() },
      body: JSON.stringify({ paths }),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.error || res.status);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "selection-" + Date.now() + ".zip";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  } catch (e) {
    alert("Download failed: " + (e.message || e));
  }
}

/** Delete every selected entry with a single confirmation. */
async function deleteFvSelection() {
  const paths = [...fvSelected];
  if (!paths.length) return;
  const ok = await customConfirm({
    title: "Delete " + paths.length + " item(s)?",
    message: "This will permanently delete the selected files and folders. This cannot be undone.",
    confirmText: "Delete",
    destructive: true,
  });
  if (!ok) return;
  let failed = [];
  for (const p of paths) {
    try {
      const res = await fetch("/api/file?path=" + encodeURIComponent(p), {
        method: "DELETE",
        headers: { "Authorization": "Bearer " + Token.get() },
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        failed.push(p.split(/[\\/]/).pop() + " (" + (e.error || res.status) + ")");
        continue;
      }
      for (const [tabId, t] of fvOpenTabs) {
        if (tabId === "files") continue;
        if (t.filePath === p) closeFvTab(tabId);
      }
    } catch (e) {
      failed.push(p.split(/[\\/]/).pop() + " (" + (e.message || e) + ")");
    }
  }
  exitFvSelectMode();
  await loadFvDir(fvCurrentPath);
  if (failed.length) alert("Deleted with " + failed.length + " failure(s):\n\n" + failed.join("\n"));
}

function switchFvTab(tabId) {
  fvActiveTab = tabId;
  for (const [id, t] of fvOpenTabs) {
    t.tabEl.classList.toggle("active", id === tabId);
    t.paneEl.classList.toggle("hidden", id !== tabId);
  }
  // Breadcrumb shows the dir path on the files tab, the file path on file tabs.
  if (tabId === "files") {
    fvBreadcrumb.textContent = fvCurrentPath; fvBreadcrumb.scrollLeft = fvBreadcrumb.scrollWidth;
    fvBreadcrumb.scrollLeft = fvBreadcrumb.scrollWidth; fvBreadcrumb.scrollLeft = fvBreadcrumb.scrollWidth;
    fvBreadcrumb.scrollLeft = fvBreadcrumb.scrollWidth;
    fvBreadcrumb.title = fvCurrentPath;
  } else {
    const t = fvOpenTabs.get(tabId);
    fvBreadcrumb.textContent = t ? t.filePath : "";
    fvBreadcrumb.title = t ? t.filePath : "";
    // Check if the file has changed on disk since it was loaded
    if (t && t.filePath && t.mtime != null) checkFileFreshness(t);
  }
  saveFvState(activeCwdRef());
}

/** Lightweight check: compare disk mtime/size to the tab's cached values.
 *  If different, mark the tab stale (shows a dot on the tab label). */
async function checkFileFreshness(tab) {
  try {
    const res = await fetch("/api/file-meta?path=" + encodeURIComponent(tab.filePath), {
      headers: { "Authorization": "Bearer " + Token.get() },
    });
    if (!res.ok) return;
    const meta = await res.json();
    if (meta.mtime !== tab.mtime || meta.size !== tab.size) {
      tab.stale = true;
      tab.tabEl.classList.add("fv-tab-stale");
    }
  } catch {}
}

/**
 * User-initiated tab close — checks for unsaved edits first. Internal
 * callers (delete/move/flush) use closeFvTab directly since the underlying
 * file is already gone or the workspace is being torn down.
 */
async function requestCloseFvTab(tabId) {
  const t = fvOpenTabs.get(tabId);
  if (t && t.isDirty && t.isDirty()) {
    const ok = await customConfirm({
      title: "Discard unsaved changes?",
      message: '"' + (t.fileName || "This file") + '" has unsaved edits.',
      confirmText: "Discard",
      destructive: true,
    });
    if (!ok) return;
    t.tabEl.classList.remove("fv-tab-dirty");
  }
  closeFvTab(tabId);
}

function closeFvTab(tabId) {
  if (tabId === "files") return; // permanent tab
  const t = fvOpenTabs.get(tabId);
  if (!t) return;
  t.tabEl.remove();
  t.paneEl.remove();
  fvOpenTabs.delete(tabId);
  if (fvActiveTab === tabId) {
    // switch to the most recently added tab, or files
    const ids = [...fvOpenTabs.keys()];
    switchFvTab(ids.length ? ids[ids.length - 1] : "files");
  } else {
    saveFvState(activeCwdRef());
  }
}

function openFileViewer(opts = {}) {
  fvIntent = opts.intent === "pick" ? "pick" : "browse";
  fvPickMode = opts.mode === "change" ? "change" : "onboard";
  fvPickState = "ready";
  fvPickError = "";
  fvOverlay.hidden = false;
  closeSidebarRef();
  const picking = fvIntent === "pick";
  fvOverlay.classList.toggle("fv-pick-mode", picking);
  fvPickRecents.hidden = !picking;
  fvPickFoot.hidden = !picking;
  ensureFilesTab();
  switchFvTab("files");
  // Always start in files view.
  setFvFolderMode("files");
  if (picking) {
    // Pick intent browses freely — no per-workspace state restore. Start at
    // the current cwd (change mode), the previously selected dir (onboard),
    // or home (empty path → server resolves to the user's home).
    renderFvPickRecents();
    updateFvPickFoot();
    const start = fvPickMode === "change" ? activeCwdRef() : selectedDirRef();
    loadFvDir(start || "");
    return;
  }
  // Restore per-workspace state if we have it: navigate back to the last
  // browsed directory and reopen the file tabs that were open. Falls back to
  // the workspace root when no saved state exists (first open / cleared).
  restoreFvState(activeCwdRef());
}

/**
 * Restore the file viewer to its last-saved state for the given workspace.
 * Reopens the saved directory and any open file tabs, then activates the tab
 * that was active when the viewer was last closed. Silently falls back to the
 * workspace root on any error or missing state.
 */
/**
 * Reset the file viewer after a workspace folder change. Closes all file tabs
 * belonging to the old workspace, clears fvCurrentPath, and — if the viewer is
 * open — reloads it for the new workspace (restoring its saved state or
 * falling back to the new root). When the viewer is closed, the reset still
 * happens so the next open starts fresh for the new workspace.
 */
function flushFvStateForCwdChange(newCwd) {
  // Close every open file tab (keep the permanent "files" tab).
  for (const id of [...fvOpenTabs.keys()]) {
    if (id !== "files") closeFvTab(id);
  }
  // Reset the directory shown on the files tab so stale state can't leak.
  fvCurrentPath = "";
  // If the viewer is open, reload it for the new workspace now; otherwise the
  // next openFileViewer() will pick up the new cwd via restoreFvState().
  if (!fvOverlay.hidden) restoreFvState(newCwd);
}

async function restoreFvState(cwd) {
  const fallback = cwd || "/";
  const saved = loadFvState(cwd);
  if (!saved || !saved.currentPath) {
    loadFvDir(fallback);
    return;
  }
  // Navigate to the saved directory first; this also resets to files view.
  await loadFvDir(saved.currentPath);
  // Reopen saved file tabs in order. loadFvFile dedups by path, so a tab
  // already present (e.g. the permanent "files" tab) won't be duplicated.
  for (const f of saved.openFiles || []) {
    try { await loadFvFile(f.filePath, f.fileName); }
    catch { /* file may have been deleted/moved — skip */ }
  }
  // Activate the tab that was active when the viewer was last closed.
  if (saved.activeFilePath) {
    for (const [id, t] of fvOpenTabs) {
      if (t.filePath === saved.activeFilePath) { switchFvTab(id); break; }
    }
  } else {
    switchFvTab("files");
  }
}

function closeFileViewer() {
  fvOverlay.hidden = true;
  if (fvIntent === "pick") {
    fvIntent = "browse";
    fvOverlay.classList.remove("fv-pick-mode");
    fvPickRecents.hidden = true;
    fvPickFoot.hidden = true;
  }
}

// ============================================================================
// Pick intent — recents row + Start/Move footer (replaces dir-picker.js)
// ============================================================================

function updateFvPickFoot() {
  if (fvIntent !== "pick") return;
  if (fvPickState === "create") {
    fvPickSelected.textContent = fvCurrentPath + " — doesn't exist yet";
    fvPickConfirm.textContent = fvPickMode === "change" ? "Create & move here" : "Create & start here";
    fvPickConfirm.disabled = false;
  } else if (fvPickState === "error") {
    fvPickSelected.textContent = fvPickError || "Can't open this folder";
    fvPickConfirm.disabled = true;
  } else {
    fvPickSelected.textContent = fvCurrentPath || "…";
    fvPickConfirm.textContent = fvPickMode === "change" ? "Move here" : "Start here";
    fvPickConfirm.disabled = !fvCurrentPath;
  }
}

function renderFvPickRecents() {
  if (fvIntent !== "pick") return;
  fvPickRecents.innerHTML = "";
  const dirs = loadRecentDirs();
  if (!dirs.length) { fvPickRecents.hidden = true; return; }
  fvPickRecents.hidden = false;
  const label = document.createElement("span");
  label.className = "picker-recents-label";
  label.textContent = "Recent";
  fvPickRecents.appendChild(label);
  for (const d of dirs) {
    const chip = document.createElement("span");
    const isLive = liveCwdsRef().has(d);
    chip.className = "picker-recent-chip" + (isLive ? " live" : "");
    chip.title = isLive ? d + " (live session)" : d;
    const name = document.createElement("span");
    name.className = "picker-recent-name";
    const segs = d.replace(/[\\/]+$/, "").split(/[\\/]/).filter(Boolean);
    name.textContent = segs.length <= 2 ? d : "…/" + segs.slice(-2).join("/");
    chip.appendChild(name);
    if (isLive) {
      const dot = document.createElement("span");
      dot.className = "picker-recent-live";
      dot.title = "Live session";
      chip.appendChild(dot);
    }
    const removeBtn = document.createElement("button");
    removeBtn.className = "picker-recent-remove";
    removeBtn.textContent = "×";
    removeBtn.title = "Remove from recents";
    removeBtn.onclick = (e) => {
      e.stopPropagation();
      haptic("tap");
      removeRecentDirRef(d);
      renderFvPickRecents();
    };
    chip.appendChild(removeBtn);
    chip.onclick = () => { haptic("tap"); loadFvDir(d); };
    fvPickRecents.appendChild(chip);
  }
}

async function confirmPick() {
  if (!fvCurrentPath) return;
  haptic("tap");
  const target = fvCurrentPath;
  if (fvPickState === "create") {
    try {
      const res = await fetch("/api/folder", {
        method: "POST",
        headers: { "Authorization": "Bearer " + Token.get(), "Content-Type": "application/json" },
        body: JSON.stringify({ path: target }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || res.status);
      }
    } catch (e) {
      fvToast("Create folder failed: " + (e.message || e));
      return;
    }
  }
  closeFileViewer();
  if (fvPickMode === "change") changeFolderRef(target);
  else { setSelectedDirRef(target); startSessionRef(); }
}

// ============================================================================
// Git-aware diff tree (see SPEC.md)
// ============================================================================
// State: the last-computed tree + the inputs that produced it, so we can
// preserve expand/collapse state across refreshes and skip refetches when
// the inputs haven't changed.
let fvDiffTreeData = null;     // last DiffTreeResult.root
let fvDiffResult = null;       // last DiffTreeResult (for summary/base)
let fvDiffExpanded = new Set(); // paths that are expanded (folders)
let fvDiffAutoCollapseThreshold = 50;
let fvDiffIntensityThreshold = 256;
let fvDiffLoading = false;
let fvBranchesKey = "";        // cache key for loaded branches (cwd)
let fvBranchesLoading = false;

/** Load branches for the current repo and populate the base-ref <select>. */
async function loadFvBranches() {
  const cwd = activeCwdRef() || fvCurrentPath || "/";
  if (cwd === fvBranchesKey || fvBranchesLoading) return;
  fvBranchesLoading = true;
  try {
    const res = await fetch("/api/diff-branches?cwd=" + encodeURIComponent(cwd), {
      headers: { "Authorization": "Bearer " + Token.get() },
    });
    if (!res.ok) return;
    const data = await res.json();
    const branches = data.branches || [];
    // Preserve current selection if any.
    const prevVal = fvDiffBase.value;
    fvDiffBase.innerHTML = "";
    if (branches.length === 0) {
      const opt = document.createElement("option");
      opt.value = "";
      opt.textContent = "(no branches)";
      fvDiffBase.appendChild(opt);
    } else {
      for (const b of branches) {
        const opt = document.createElement("option");
        opt.value = b.short;
        const prefix = b.isCurrent ? "● " : (b.isRemote ? "origin/" : "");
        opt.textContent = prefix + b.short + (b.isCurrent ? " (current)" : "");
        fvDiffBase.appendChild(opt);
      }
    }
    // Try to restore previous selection, or pick the first non-current branch.
    if (prevVal && [...fvDiffBase.options].some(o => o.value === prevVal)) {
      fvDiffBase.value = prevVal;
    } else {
      // Default to the first non-current branch (the base to compare against).
      const firstNonCurrent = branches.find(b => !b.isCurrent);
      fvDiffBase.value = firstNonCurrent ? firstNonCurrent.short : (branches[0]?.short || "");
    }
    fvBranchesKey = cwd;
  } catch {
    /* ignore — the select will just be empty */
  } finally {
    fvBranchesLoading = false;
  }
}

/** Compute the log-scale intensity in [0,1] for a node's badge tint. */
function diffIntensity(added, deleted) {
  const total = added + deleted;
  if (total <= 0) return 0;
  return Math.min(Math.log2(total + 1) / Math.log2(fvDiffIntensityThreshold), 1);
}

/** Build a CSS color for the badge background based on intensity + sign. */
function diffBadgeStyle(added, deleted) {
  const intensity = diffIntensity(added, deleted);
  if (intensity <= 0) return "";
  // Dominant direction picks the hue; intensity picks the alpha.
  const dominant = added >= deleted ? "var(--green-bg)" : "var(--red-bg)";
  // Scale alpha-ish via box-shadow inset opacity proxy: use rgba layering.
  return `background: ${dominant}; box-shadow: inset 0 0 0 1px ${dominant};`;
}

/** Format the badge text: +N -M · K files (folders) or +N -M (files). */
function diffBadgeText(node) {
  if (node.binary) return "(binary)";
  const parts = [];
  if (node.added > 0) parts.push("+" + node.added);
  if (node.deleted > 0) parts.push("-" + node.deleted);
  if (node.type === "folder" && node.fileCount > 0) parts.push("· " + node.fileCount + " file" + (node.fileCount === 1 ? "" : "s"));
  return parts.length ? parts.join(" ") : "0";
}

/** Sort a node's children in place according to the current sort mode. */
function diffSortChildren(children) {
  const mode = fvDiffSort.value;
  const byMagnitude = (a, b) => (b.added + b.deleted) - (a.added + a.deleted);
  const cmp = (a, b) => {
    // Folders first, then files, within each group apply the sort.
    if (a.type !== b.type) return a.type === "folder" ? -1 : 1;
    switch (mode) {
      case "name": return a.name.localeCompare(b.name, undefined, { numeric: true });
      case "added": return b.added - a.added;
      case "deleted": return b.deleted - a.deleted;
      case "net": return (b.added - b.deleted) - (a.added - a.deleted);
      case "magnitude":
      default: return byMagnitude(a, b);
    }
  };
  children.sort(cmp);
  return children;
}

/** Recursively filter the tree by a path substring; prunes empty folders. */
function diffFilterTree(node, q) {
  if (!q) return node;
  const ql = q.toLowerCase();
  if (node.type === "file") {
    return node.path.toLowerCase().includes(ql) ? node : null;
  }
  const keepSelf = node.path.toLowerCase().includes(ql);
  const kept = [];
  for (const child of node.children) {
    const k = diffFilterTree(child, q);
    if (k) kept.push(k);
  }
  if (kept.length || keepSelf) {
    return { ...node, children: kept };
  }
  return null;
}

/** Fetch the diff tree from the server and render it. */
async function loadFvDiff(force = false) {
  if (fvDiffLoading) return;
  const cwd = activeCwdRef() || fvCurrentPath || "/";
  const base = fvDiffBase.value;
  const mode = fvDiffModeSelect.value;
  const filter = fvDiffFilter.value.trim();
  const key = `${cwd}|${base}|${mode}`;
  if (!force && key === fvDiffLastKey && fvDiffTreeData) {
    renderFvDiff();
    return;
  }
  fvDiffLoading = true;
  fvDiffTree.innerHTML = '<div class="fv-diff-empty">Loading diff…</div>';
  fvDiffSummary.textContent = "";
  try {
    const params = new URLSearchParams({ cwd, mode });
    if (base) params.set("base", base);
    const res = await fetch("/api/diff-tree?" + params.toString(), {
      headers: { "Authorization": "Bearer " + Token.get() },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || res.status);
    }
    const data = await res.json();
    fvDiffResult = data;
    fvDiffTreeData = data.root;
    fvDiffLastKey = key;
    // Auto-select the resolved base ref if no explicit base was chosen.
    if (!base && data.base) {
      const opts = [...fvDiffBase.options];
      if (opts.some(o => o.value === data.base)) {
        fvDiffBase.value = data.base;
      } else {
        // Resolved base not in dropdown — add it as an extra option.
        const opt = document.createElement("option");
        opt.value = data.base;
        opt.textContent = data.base + " (auto)";
        fvDiffBase.appendChild(opt);
        fvDiffBase.value = data.base;
      }
    }
    if (data.error) {
      fvDiffTree.innerHTML = '<div class="fv-diff-empty">' + escapeHtml(data.error) + "</div>";
      fvDiffSummary.textContent = "";
      fvDiffLoading = false;
      return;
    }
    renderFvDiff(filter);
  } catch (e) {
    fvDiffTree.innerHTML = '<div class="fv-diff-empty">Error: ' + escapeHtml(e.message || e) + "</div>";
  } finally {
    fvDiffLoading = false;
  }
}

/** Render the summary line + tree from fvDiffTreeData. */
function renderFvDiff(filterOverride) {
  const filter = (filterOverride !== undefined ? filterOverride : fvDiffFilter.value.trim());
  if (!fvDiffTreeData || !fvDiffResult) {
    fvDiffTree.innerHTML = '<div class="fv-diff-empty">No changes vs base.</div>';
    fvDiffSummary.textContent = "";
    return;
  }
  const r = fvDiffResult;
  const baseLabel = r.base ? r.base : "(no base)";
  // Scope the tree to the current folder: navigate the trie to the node whose
  // path matches the current directory (relative to the repo root).
  const scope = currentFolderRelPath();
  let root = fvDiffTreeData;
  if (scope) {
    const scoped = subtreeAtPath(root, scope);
    if (!scoped) {
      fvDiffTree.innerHTML = '<div class="fv-diff-empty">No changes in this folder vs base.</div>';
      fvDiffSummary.textContent = "";
      return;
    }
    root = scoped;
  }
  // Summary reflects the scoped subtree, not the whole repo.
  fvDiffSummary.innerHTML =
    '<span class="fv-diff-sum-base">base: <code>' + escapeHtml(baseLabel) + '</code></span>' +
    '<span class="fv-diff-sum-mode">' + escapeHtml(r.mode) + "</span>" +
    '<span class="fv-diff-sum-add">+' + root.added + "</span>" +
    '<span class="fv-diff-sum-del">-' + root.deleted + "</span>" +
    '<span class="fv-diff-sum-files">' + root.fileCount + " file" + (root.fileCount === 1 ? "" : "s") + "</span>";

  if (filter) {
    const filtered = diffFilterTree(root, filter);
    if (!filtered) {
      fvDiffTree.innerHTML = '<div class="fv-diff-empty">No paths match "' + escapeHtml(filter) + '".</div>';
      return;
    }
    root = filtered;
  }
  fvDiffTree.innerHTML = "";
  // Render top-level children (skip the root node itself).
  const frag = document.createDocumentFragment();
  for (const child of diffSortChildren([...root.children])) {
    frag.appendChild(renderDiffNode(child, 0));
  }
  if (!frag.childNodes.length) {
    fvDiffTree.innerHTML = '<div class="fv-diff-empty">No changes in this folder vs base.</div>';
    return;
  }
  fvDiffTree.appendChild(frag);
}

/**
 * Repo-relative path of the folder currently being browsed (forward slashes,
 * no leading slash). Returns "" when at the repo root so the whole tree shows.
 */
function currentFolderRelPath() {
  const cwd = (activeCwdRef() || "").replace(/\\/g, "/");
  const cur = (fvCurrentPath || "").replace(/\\/g, "/");
  if (!cur || !cwd) return "";
  let rel = cur;
  if (rel.toLowerCase().startsWith(cwd.toLowerCase())) {
    rel = rel.slice(cwd.length);
  }
  rel = rel.replace(/^\/+/, "").replace(/\/+$/, "");
  return rel;
}

/** Walk the diff trie to the node at `path` (repo-relative, "/"-separated). */
function subtreeAtPath(root, path) {
  if (!path) return root;
  const segs = path.split("/").filter(Boolean);
  let cur = root;
  for (const seg of segs) {
    const next = cur.children.find((c) => c.name === seg && c.type === "folder");
    if (!next) return null;
    cur = next;
  }
  return cur;
}

/** Render one node (file or folder) at the given indent depth. */
function renderDiffNode(node, depth) {
  const row = document.createElement("div");
  row.className = "fv-diff-row" + (node.type === "folder" ? " fv-diff-folder" : " fv-diff-file");
  row.style.paddingLeft = 8 + depth * 16 + "px";

  const icon = document.createElement("span");
  icon.className = "fv-diff-icon";
  let expanded = false;
  if (node.type === "folder") {
    expanded = fvDiffExpanded.has(node.path) || node.children.length <= fvDiffAutoCollapseThreshold;
    icon.textContent = expanded ? "▾" : "▸";
  } else {
    icon.innerHTML = node.binary ? FV_ICON_ARCHIVE : FV_ICON_FILE;
  }
  row.appendChild(icon);

  const name = document.createElement("span");
  name.className = "fv-diff-name";
  name.textContent = node.name;
  if (node.type === "file" && node.entry && node.entry.oldPath) {
    name.title = "renamed from " + node.entry.oldPath;
  }
  row.appendChild(name);

  const badge = document.createElement("span");
  badge.className = "fv-diff-badge";
  badge.textContent = diffBadgeText(node);
  badge.title = "+" + node.added + " / -" + node.deleted + (node.type === "folder" ? " · " + node.fileCount + " files" : "");
  // Split into added/deleted spans for green/red coloring.
  badge.innerHTML = "";
  if (node.binary) {
    badge.textContent = "(binary)";
  } else {
    if (node.added > 0) {
      const a = document.createElement("span"); a.className = "fv-diff-add"; a.textContent = "+" + node.added;
      badge.appendChild(a);
    }
    if (node.deleted > 0) {
      const d = document.createElement("span"); d.className = "fv-diff-del"; d.textContent = "-" + node.deleted;
      badge.appendChild(d);
    }
    if (node.type === "folder" && node.fileCount > 0) {
      const f = document.createElement("span"); f.className = "fv-diff-files"; f.textContent = "· " + node.fileCount + " file" + (node.fileCount === 1 ? "" : "s");
      badge.appendChild(f);
    }
    if (node.added === 0 && node.deleted === 0) {
      badge.textContent = "0";
    }
  }
  const style = diffBadgeStyle(node.added, node.deleted);
  if (style) badge.setAttribute("style", style);
  row.appendChild(badge);

  if (node.type === "folder") {
    row.addEventListener("click", (e) => {
      e.stopPropagation();
      haptic("tap");
      if (fvDiffExpanded.has(node.path)) fvDiffExpanded.delete(node.path);
      else fvDiffExpanded.add(node.path);
      renderFvDiff();
    });
    const kids = document.createElement("div");
    kids.className = "fv-diff-children";
    if (expanded) {
      for (const child of diffSortChildren([...node.children])) {
        kids.appendChild(renderDiffNode(child, depth + 1));
      }
    } else {
      kids.hidden = true;
    }
    // Replace the simple row with a wrapper containing row + kids.
    const wrap = document.createElement("div");
    wrap.appendChild(row);
    wrap.appendChild(kids);
    return wrap;
  } else {
    row.addEventListener("click", (e) => {
      e.stopPropagation();
      haptic("tap");
      openFvDiffFile(node.path, node.name);
    });
    return row;
  }
}

/**
 * Open a file viewer tab for a diff-tree entry. Reuses an existing tab for the
 * same path if one is already open (flipping it to Diff view), otherwise opens
 * a fresh unified viewer tab with Diff as the initial view. The unified tab
 * carries a File ⇄ Diff toggle in its header (see buildFvViewToggle).
 */
function openFvDiffFile(relPath, name) {
  // The diff tree gives repo-relative paths (e.g. "data/settings.json"), but
  // loadFvFile → /api/file expects absolute paths. Prepend the cwd to get
  // an absolute path. /api/diff-file still works because git resolves
  // absolute paths within the repo.
  const cwd = (activeCwdRef() || fvCurrentPath || "").replace(/\/+$/, "");
  const absPath = (relPath.startsWith("/") || /^[A-Za-z]:[\\/]/.test(relPath))
    ? relPath
    : (cwd + "/" + relPath);
  // Reuse any existing tab for this path (file or diff origin).
  for (const [id, t] of fvOpenTabs) {
    if (t.filePath === absPath) {
      switchFvTab(id);
      if (t.setView) t.setView("diff");
      return;
    }
  }
  loadFvFile(absPath, name, { initialView: "diff" });
}

/** Fetch the unified diff for a single path and render it into a <pre>. */
async function loadFileDiffInto(pre, filePath) {
  const cwd = activeCwdRef() || fvCurrentPath || "/";
  const base = fvDiffBase.value;
  const mode = fvDiffModeSelect.value;
  // Convert absolute path to repo-relative for cleaner git diff output.
  let relPath = filePath;
  if (filePath.startsWith("/")) {
    const cwdNorm = cwd.replace(/\/+$/, "");
    if (filePath.startsWith(cwdNorm + "/")) relPath = filePath.slice(cwdNorm.length + 1);
  }
  pre.textContent = "Loading diff…";
  try {
    const params = new URLSearchParams({ cwd, path: relPath, mode });
    if (base) params.set("base", base);
    const res = await fetch("/api/diff-file?" + params.toString(), {
      headers: { "Authorization": "Bearer " + Token.get() },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || res.status);
    }
    const text = await res.text();
    pre.textContent = text || "(no changes)";
    colorizeDiffPre(pre);
  } catch (e) {
    pre.textContent = "Error: " + (e.message || e);
  }
}

/**
 * Build a File ⇄ Diff toggle element. `onFile`/`onDiff` are called whenever the
 * mode changes (including the initial set). Returns the element plus a setMode
 * function and a getMode getter.
 */
function buildFvViewToggle({ onFile, onDiff, initial = "file" }) {
  const wrap = document.createElement("div");
  wrap.className = "fv-view-toggle";
  const fileBtn = document.createElement("button");
  fileBtn.className = "fv-view-toggle-btn";
  fileBtn.type = "button";
  fileBtn.textContent = "File";
  const diffBtn = document.createElement("button");
  diffBtn.className = "fv-view-toggle-btn";
  diffBtn.type = "button";
  diffBtn.textContent = "Diff";
  wrap.appendChild(fileBtn);
  wrap.appendChild(diffBtn);
  let mode = null;
  function setMode(m) {
    if (m === mode) return;
    mode = m;
    fileBtn.classList.toggle("active", m === "file");
    diffBtn.classList.toggle("active", m === "diff");
    if (m === "file") onFile(); else onDiff();
  }
  fileBtn.addEventListener("click", (e) => { e.stopPropagation(); haptic("tap"); setMode("file"); });
  diffBtn.addEventListener("click", (e) => { e.stopPropagation(); haptic("tap"); setMode("diff"); });
  setMode(initial);
  return { el: wrap, setMode, getMode: () => mode };
}

/** Apply green/red/dim coloring to unified-diff lines in a <pre>. */
function colorizeDiffPre(pre) {
  const lines = pre.textContent.split("\n");
  pre.innerHTML = "";
  for (const line of lines) {
    const span = document.createElement("span");
    span.className = "fv-diff-line";
    if (line.startsWith("+") && !line.startsWith("+++")) span.classList.add("fv-diff-line-add");
    else if (line.startsWith("-") && !line.startsWith("---")) span.classList.add("fv-diff-line-del");
    else if (line.startsWith("@@")) span.classList.add("fv-diff-line-hunk");
    else if (line.startsWith("diff ") || line.startsWith("index ") || line.startsWith("---") || line.startsWith("+++")) span.classList.add("fv-diff-line-meta");
    span.textContent = line + "\n";
    pre.appendChild(span);
  }
}

// Wire up the diff toolbar controls.
fvDiffRefresh.addEventListener("click", () => { haptic("tap"); loadFvDiff(true); });
fvDiffBase.addEventListener("change", () => loadFvDiff(true));
fvDiffModeSelect.addEventListener("change", () => loadFvDiff(true));
fvDiffSort.addEventListener("change", () => renderFvDiff());
fvDiffFilter.addEventListener("input", () => renderFvDiff());
// Keyboard shortcuts. Ctrl/Cmd+A selects all rows while the Files tab is
// visible; the rest only fire while the folder diff pane is visible.
document.addEventListener("keydown", (e) => {
  if (fvOverlay.hidden || fvActiveTab !== "files") return;
  if (e.target && (e.target.tagName === "INPUT" || e.target.tagName === "SELECT" || e.target.tagName === "TEXTAREA")) return;
  if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "a" && fvFolderMode === "files") {
    e.preventDefault();
    haptic("tap");
    selectAllFvRows();
    return;
  }
  if (fvFolderMode !== "diff") return;
  if (e.target && (e.target.tagName === "INPUT" || e.target.tagName === "SELECT" || e.target.tagName === "TEXTAREA")) return;
  if (e.key === "r") { e.preventDefault(); loadFvDiff(true); }
  if (e.key === "/") { e.preventDefault(); fvDiffFilter.focus(); }
});

async function loadFvDir(path) {
  fvList.innerHTML = "";
  ensureFilesTab();
  switchFvTab("files");
  // Navigating to a folder resets to files view. The diff tree data is
  // repo-wide, so we keep it cached and just re-scope on the next toggle.
  setFvFolderMode("files");
  // Navigating to a new folder invalidates the current selection (those paths
  // are no longer visible here). The clipboard is preserved for paste-into.
  if (fvSelectMode) exitFvSelectMode();
  // Clear the per-folder filter when navigating.
  fvFilter.value = "";
  try {
    const res = await fetch("/api/browse?path=" + encodeURIComponent(path) + (fvShowHidden ? "&hidden=1" : ""));
    if (!res.ok) { fvList.innerHTML = '<div class="fv-item" style="color:var(--red)">Error: ' + res.status + "</div>"; return; }
    const data = await res.json();
    fvCurrentPath = data.path;
    fvHomeDir = data.home || fvHomeDir;
    fvBreadcrumb.textContent = data.path;
    fvPathInput.value = data.path;
    fvParentPath = data.parent;
    if (data.error) {
      // Path didn't resolve. ENOENT → the folder doesn't exist yet (pick
      // mode offers "Create & start here"); anything else is a hard error.
      fvEntries = [];
      fvPickState = data.code === "ENOENT" ? "create" : "error";
      fvPickError = data.error;
      fvList.innerHTML = '<div class="fv-item" style="color:var(--red)"></div>';
      fvList.firstElementChild.textContent = data.code === "ENOENT"
        ? "Folder doesn't exist: " + data.path
        : "Error: " + data.error;
      updateFvPickFoot();
      return;
    }
    fvPickState = "ready";
    fvPickError = "";
    fvEntries = data.entries || [];
    saveFvState(activeCwdRef());
    renderFvList();
    updateFvPickFoot();
  } catch (e) {
    fvList.innerHTML = '<div class="fv-item" style="color:var(--red)">Error: ' + (e.message || e) + "</div>";
  }
}

/** Render the current directory listing from fvEntries, applying the
 *  toolbar filter and the user's sort preference. Re-rendered without a
 *  network round-trip when the filter text or sort changes. */
function renderFvList() {
  if (!fvCurrentPath) return;
  fvList.innerHTML = "";
  const q = fvFilter.value.trim().toLowerCase();
  if (fvParentPath !== null) {
    const up = document.createElement("div");
    up.className = "fv-item";
    up.innerHTML = '<span class="fv-icon">' + FV_ICON_DIR + '</span><span class="fv-name">..</span>';
    up.onclick = () => { haptic("tap"); loadFvDir(fvParentPath); };
    fvList.appendChild(up);
  }
  const entries = fvEntries
    .filter((e) => !q || e.name.toLowerCase().includes(q))
    .sort(fvSortCmp);
  fvRenderedEntries = entries;
  if (!entries.length) {
    const empty = document.createElement("div");
    empty.className = "fv-empty";
    empty.textContent = q ? 'No matches for "' + q + '"' : "Empty folder";
    fvList.appendChild(empty);
    return;
  }
  for (const [rowIdx, entry] of entries.entries()) {
    const item = document.createElement("div");
    item.className = "fv-item";
    item.dataset.path = fvCurrentPath + "/" + entry.name;
    const entryPath = item.dataset.path;
    const ext = entry.name.split(".").pop()?.toLowerCase() || "";
    const isText = !entry.isDir && (TEXT_EXTS.has(ext) || !entry.name.includes("."));
    const isBinaryViewable = !entry.isDir && BINARY_VIEWABLE_EXTS.has(ext);
    const media = !entry.isDir ? mediaKind(ext) : null;
    const viewable = isText || isBinaryViewable || !!media;
    // Checkbox shown only in select mode (CSS toggles display).
    const check = document.createElement("span");
    check.className = "fv-check";
    item.appendChild(check);
    if (entry.isDir) {
      item.dataset.isdir = "1";
      const icon = document.createElement("span");
      icon.className = "fv-icon";
      icon.innerHTML = FV_ICON_DIR;
      item.appendChild(icon);
      const name = document.createElement("span");
      name.className = "fv-name";
      name.textContent = entry.name;
      item.appendChild(name);
    } else {
      const ficon = svgIcon(svgFileIcon(entry.name, false, media));
      item.innerHTML = "";
      item.appendChild(check);
      const ic = document.createElement("span");
      ic.className = "fv-icon";
      ic.innerHTML = ficon;
      item.appendChild(ic);
      const name = document.createElement("span");
      name.className = "fv-name";
      name.textContent = entry.name;
      item.appendChild(name);
      const size = document.createElement("span");
      size.className = "fv-size";
      if (entry.size != null) size.textContent = formatSize(entry.size);
      item.appendChild(size);
      if (!viewable) {
        item.style.opacity = "0.5";
        item.title = "Binary file — not viewable";
      }
    }
    // Last-modified stamp for both files and folders.
    const mt = document.createElement("span");
    mt.className = "fv-mtime";
    mt.textContent = formatTimeAgo(entry.mtime);
    if (entry.mtime) mt.title = new Date(entry.mtime).toLocaleString();
    item.appendChild(mt);
    // Click behavior:
    //   Shift+click     → select contiguous range from the last-clicked row
    //   Ctrl/Cmd+click  → toggle this row in/out of the selection
    //   plain click     → toggle (in select mode) or open
    item.addEventListener("click", (e) => {
      if (e.shiftKey) {
        e.preventDefault();
        haptic("tap");
        selectFvRange(rowIdx);
        return;
      }
      if (fvSelectMode) {
        e.preventDefault();
        haptic("tap");
        toggleFvRow(entryPath);
        fvAnchorPath = entryPath;
        return;
      }
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        haptic("tap");
        enterFvSelectMode(entryPath);
        fvAnchorPath = entryPath;
        return;
      }
      haptic("tap");
      fvAnchorPath = entryPath;
      if (entry.isDir) loadFvDir(entryPath);
      else if (viewable) loadFvFile(entryPath, entry.name);
    });
    // Delete button (trash icon) — opens a confirm modal before deleting.
    const delBtn = document.createElement("button");
    delBtn.className = "fv-delete-btn";
    delBtn.title = "Delete " + entry.name;
    delBtn.innerHTML = svgIcon("trash", 14);
    delBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      haptic("tap");
      deleteFvEntry(entryPath, entry.name, entry.isDir);
    });
    item.appendChild(delBtn);
    // Context menu (right-click / long-press) with file/folder actions.
    attachFvContextMenu(item, {
      entryPath,
      name: entry.name,
      isDir: entry.isDir,
      viewable,
    });
    // Restore selection visual if this row is in the selection set.
    if (fvSelected.has(entryPath)) item.classList.add("fv-item-selected");
    fvList.appendChild(item);
  }
}

/**
 * Confirm and delete a file or directory from the file viewer.
 * Asks for confirmation via a modal, then calls DELETE /api/file and refreshes
 * the current folder listing. If the deleted file was open in a tab, closes it.
 */
async function deleteFvEntry(filePath, name, isDir) {
  const kind = isDir ? "folder" : "file";
  const ok = await customConfirm({
    title: "Delete " + kind,
    message: 'Are you sure you want to delete "' + name + '"?' + (isDir ? " This will remove the folder and all of its contents." : ""),
    confirmText: "Delete",
    destructive: true,
  });
  if (!ok) return;
  try {
    const res = await fetch("/api/file?path=" + encodeURIComponent(filePath), {
      method: "DELETE",
      headers: { "Authorization": "Bearer " + Token.get() },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || res.status);
    }
    // Close any open viewer tab for the deleted file.
    for (const [tabId, t] of fvOpenTabs) {
      if (tabId === "files") continue;
      if (t.filePath === filePath) closeFvTab(tabId);
    }
    await loadFvDir(fvCurrentPath);
  } catch (e) {
    alert("Delete failed: " + (e.message || e));
  }
}

/**
 * Download a file or folder to the user's local machine. Files are downloaded
 * as-is; folders are zipped server-side. Uses a blob + anchor download so the
 * auth header can be attached to the request.
 */
async function downloadFvEntry(entryPath, name, isDir) {
  const downloadName = isDir ? name + ".zip" : name;
  try {
    const res = await fetch("/api/download?path=" + encodeURIComponent(entryPath), {
      headers: { "Authorization": "Bearer " + Token.get() },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || res.status);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = downloadName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  } catch (e) {
    alert("Download failed: " + (e.message || e));
  }
}

/**
 * Prompt the user to pick local files and upload them into the given directory.
 * Uses a transient <input type="file" multiple> element.
 */
function uploadFvEntry(dir) {
  const input = document.createElement("input");
  input.type = "file";
  input.multiple = true;
  input.style.display = "none";
  document.body.appendChild(input);
  input.addEventListener("change", async () => {
    const files = Array.from(input.files || []);
    input.remove();
    if (!files.length) return;
    await uploadFvFiles(dir, files);
  });
  // Clean up if the user cancels the picker.
  input.addEventListener("cancel", () => input.remove());
  input.click();
}

/**
 * Show a small context menu at the given viewport coordinates. `actions` is an
 * ordered list of { label, icon, destructive, onClick }. The menu dismisses on
 * outside click, Escape, or scroll. Returns nothing.
 */
function showFvContextMenu(x, y, actions) {
  // Remove any existing menu first.
  document.querySelectorAll(".fv-menu").forEach((m) => m.remove());

  const menu = document.createElement("div");
  menu.className = "fv-menu";
  for (const act of actions) {
    const btn = document.createElement("button");
    btn.className = "fv-menu-item" + (act.destructive ? " fv-menu-destructive" : "");
    if (act.icon) {
      const ic = document.createElement("span");
      ic.className = "fv-menu-icon";
      ic.innerHTML = act.icon;
      btn.appendChild(ic);
    }
    const lbl = document.createElement("span");
    lbl.className = "fv-menu-label";
    lbl.textContent = act.label;
    btn.appendChild(lbl);
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      haptic("tap");
      menu.remove();
      document.removeEventListener("keydown", keyHandler);
      window.removeEventListener("scroll", dismiss, true);
      act.onClick();
    });
    menu.appendChild(btn);
  }
  document.body.appendChild(menu);
  // Position, clamping to viewport.
  const rect = menu.getBoundingClientRect();
  const px = Math.min(x, window.innerWidth - rect.width - 8);
  const py = Math.min(y, window.innerHeight - rect.height - 8);
  menu.style.left = Math.max(8, px) + "px";
  menu.style.top = Math.max(8, py) + "px";

  function dismiss() { menu.remove(); document.removeEventListener("keydown", keyHandler); window.removeEventListener("scroll", dismiss, true); }
  function keyHandler(e) { if (e.key === "Escape") { e.preventDefault(); dismiss(); } }
  // Defer attaching outside-click so the opening event doesn't immediately close it.
  requestAnimationFrame(() => {
    document.addEventListener("click", dismiss, { once: true });
    document.addEventListener("contextmenu", dismiss, { once: true });
  });
  document.addEventListener("keydown", keyHandler);
  window.addEventListener("scroll", dismiss, true);
}

/**
 * Attach right-click (desktop) and long-press (touch) handlers to a file-viewer
 * list item, opening a context menu with the relevant actions for the entry.
 */
function attachFvContextMenu(item, { entryPath, name, isDir, viewable }) {
  const open = () => {
    // Opening from the context menu exits select mode first so the open
    // isn't re-intercepted by the select-mode click handler.
    if (fvSelectMode) exitFvSelectMode();
    if (isDir) loadFvDir(entryPath);
    else if (viewable) loadFvFile(entryPath, name);
  };
  const buildActions = () => {
    const actions = [];
    if (isDir || viewable) {
      actions.push({ label: isDir ? "Open folder" : "Open", icon: isDir ? FV_ICON_DIR : FV_ICON_FILE, onClick: open });
    }
    // "Select" enters multi-select mode with this row pre-selected. On
    // touch this is also achieved by the long-press gesture itself, but
    // the menu item gives desktop right-click users an explicit entry.
    if (!fvSelectMode) {
      actions.push({ label: "Select", icon: "☑", onClick: () => enterFvSelectMode(entryPath) });
    }
    actions.push({ label: isDir ? "Download as .zip" : "Download", icon: svgIcon("download", 14), onClick: () => downloadFvEntry(entryPath, name, isDir) });
    if (isDir) {
      actions.push({ label: "Upload here", icon: "⬆", onClick: () => uploadFvEntry(entryPath) });
    }
    // Pin affordances — session pin (or global when fv has no session
    // context) + an explicit global tier for "every session". Session key
    // resolved at menu-build time so a session started after fv opened
    // still gets its pins.
    if (!isDir && pinOpsRef) {
      const sessionKey = fvSessionKey();
      const tier = pinOpsRef.filePinTier(entryPath, sessionKey);
      const inSession = !!sessionKey;
      actions.push({
        label: tier === "session" ? "Unpin" : (inSession ? "Pin to session" : "Pin globally"),
        icon: svgIcon(tier === "session" ? "pinOff" : "pin", 14),
        onClick: () => inSession ? pinOpsRef.toggleFilePin(entryPath, name, fvSessionKey()) : pinOpsRef.toggleFilePinGlobal(entryPath, name),
      });
      if (inSession) {
        actions.push({
          label: tier === "global" ? "Unpin globally" : "Pin globally",
          icon: svgIcon("globe", 14),
          onClick: () => pinOpsRef.toggleFilePinGlobal(entryPath, name),
        });
      }
    }
    actions.push({ label: "Rename", icon: "✎", onClick: () => renameFvEntry(entryPath, name, isDir) });
    actions.push({ label: "Copy path", icon: svgIcon("copy", 14), onClick: () => copyFvPath(entryPath) });
    actions.push({ label: "Delete", icon: svgIcon("trash", 14), destructive: true, onClick: () => deleteFvEntry(entryPath, name, isDir) });
    return actions;
  };

  item.addEventListener("contextmenu", (e) => {
    e.preventDefault();
    e.stopPropagation();
    haptic("tap");
    showFvContextMenu(e.clientX, e.clientY, buildActions());
  });

  // Long-press support for touch devices. The long-press does double duty:
  // it opens the per-row context menu AND enters multi-select mode with
  // this row pre-selected, so mobile users get both affordances in one
  // gesture (matching native file-manager behavior).
  let pressTimer = null;
  let longPressed = false;
  let startX = 0, startY = 0;
  item.addEventListener("touchstart", (e) => {
    if (e.touches.length !== 1) return;
    const t = e.touches[0];
    startX = t.clientX; startY = t.clientY;
    longPressed = false;
    pressTimer = setTimeout(() => {
      longPressed = true;
      haptic("tap");
      enterFvSelectMode(entryPath);
      showFvContextMenu(t.clientX, t.clientY, buildActions());
    }, 500);
  }, { passive: true });
  const cancel = () => { if (pressTimer) { clearTimeout(pressTimer); pressTimer = null; } };
  item.addEventListener("touchmove", (e) => {
    if (!pressTimer) return;
    const t = e.touches[0];
    if (Math.abs(t.clientX - startX) > 10 || Math.abs(t.clientY - startY) > 10) cancel();
  }, { passive: true });
  item.addEventListener("touchend", (e) => {
    if (longPressed) { e.preventDefault(); e.stopPropagation(); }
    cancel();
  });
  item.addEventListener("touchcancel", cancel);
}

/**
 * Create a file viewer tab + pane for a binary (non-editable) file.
 * Returns { tabId, pane, head, meta, fileBody, setView }. The optional onClose
 * callback is invoked when the tab is closed (use it to revoke blob URLs, etc.).
 * Renderers append their content to `fileBody` so the File ⇄ Diff toggle can
 * swap it out for the diff view.
 */
function createFvBinaryTab(filePath, fileName, icon, onClose, initialView = "file") {
  ensureFilesTab();
  const tabId = "file_" + Date.now() + "_" + Math.random().toString(36).slice(2, 6);

  // Tab element
  const tab = document.createElement("div");
  tab.className = "fv-tab";
  tab.dataset.tabId = tabId;
  const label = document.createElement("span");
  label.className = "fv-tab-label";
  label.textContent = icon + " " + fileName;
  const closeBtn = document.createElement("button");
  closeBtn.className = "fv-tab-close";
  closeBtn.title = "Close tab";
  closeBtn.textContent = "×";
  closeBtn.addEventListener("click", (e) => {
    e.stopPropagation(); haptic("tap");
    if (onClose) onClose();
    requestCloseFvTab(tabId);
  });
  tab.appendChild(label);
  tab.appendChild(closeBtn);
  tab.addEventListener("click", () => { haptic("tap"); switchFvTab(tabId); });
  fvTabs.appendChild(tab);

  // Pane element
  const pane = document.createElement("div");
  pane.className = "fv-pane";
  const head = document.createElement("div");
  head.className = "fv-viewer-head";
  const headInfo = document.createElement("div");
  headInfo.className = "fv-head-info";
  const fn = document.createElement("span");
  fn.className = "fv-filename";
  fn.textContent = fileName;
  const meta = document.createElement("span");
  meta.className = "fv-meta";
  meta.textContent = "Loading…";
  headInfo.appendChild(fn);
  headInfo.appendChild(meta);
  // File ⇄ Diff toggle (shared with the text-file viewer).
  const fileBody = document.createElement("div");
  fileBody.className = "fv-file-body";
  const diffBody = document.createElement("pre");
  diffBody.className = "fv-diff-pre fv-diff-body hidden";
  let diffLoaded = false;
  const viewToggle = buildFvViewToggle({
    initial: initialView,
    onFile: () => { fileBody.classList.remove("hidden"); diffBody.classList.add("hidden"); },
    onDiff: () => {
      fileBody.classList.add("hidden");
      diffBody.classList.remove("hidden");
      if (!diffLoaded) { diffLoaded = true; loadFileDiffInto(diffBody, filePath); }
    },
  });
  head.appendChild(headInfo);
  head.appendChild(viewToggle.el);
  // Refresh button for binary files — caller wires the click handler.
  const refreshBtn = document.createElement("button");
  refreshBtn.className = "fv-action-btn fv-refresh-btn";
  refreshBtn.textContent = "↻";
  refreshBtn.title = "Refresh from disk";
  // Copy button — hidden by default; renderers that can put the file's
  // contents on the clipboard (images, CSV) un-hide it and wire the click.
  const copyBtn = document.createElement("button");
  copyBtn.className = "fv-action-btn fv-copy-btn";
  copyBtn.textContent = "Copy";
  copyBtn.title = "Copy contents";
  copyBtn.style.display = "none";
  const actions = document.createElement("span");
  actions.className = "fv-actions";
  const pinBtn = mkFvPinBtn(filePath, fileName);
  actions.appendChild(pinBtn);
  actions.appendChild(copyBtn);
  actions.appendChild(refreshBtn);
  head.appendChild(actions);
  pane.appendChild(head);
  pane.appendChild(fileBody);
  pane.appendChild(diffBody);
  fvOverlay.querySelector(".fv-modal").appendChild(pane);

  const tabRecord = { tabEl: tab, paneEl: pane, fileName, filePath, setView: viewToggle.setView, mtime: null, size: null, icon, zoom: 1 };
  fvOpenTabs.set(tabId, tabRecord);
  actions.appendChild(mkFvZoomCtrls(tabRecord, [fileBody, diffBody]));
  switchFvTab(tabId);
  return { tabId, pane, head, meta, fileBody, refreshBtn, copyBtn, setView: viewToggle.setView };
}

/**
 * Zoom controls (− / % / +) for an open file tab. Applies CSS zoom to the
 * tab's content bodies — works uniformly for text, tables, docx, pptx,
 * media, and iframed renderers (pdf/html). Zoom level is kept on the tab
 * record so each file remembers its own level.
 */
function mkFvZoomCtrls(tabRecord, targets) {
  const wrap = document.createElement("span");
  wrap.className = "fv-zoom";
  const out = document.createElement("button");
  out.className = "fv-action-btn";
  out.textContent = "−";
  out.title = "Zoom out";
  const pct = document.createElement("button");
  pct.className = "fv-action-btn fv-zoom-pct";
  pct.title = "Reset zoom";
  const inBtn = document.createElement("button");
  inBtn.className = "fv-action-btn";
  inBtn.textContent = "+";
  inBtn.title = "Zoom in";
  const apply = () => {
    pct.textContent = Math.round(tabRecord.zoom * 100) + "%";
    for (const el of targets) el.style.zoom = tabRecord.zoom;
  };
  const step = (dir) => {
    haptic("tap");
    tabRecord.zoom = Math.min(4, Math.max(0.25, tabRecord.zoom * (dir > 0 ? 1.15 : 1 / 1.15)));
    apply();
  };
  out.addEventListener("click", (e) => { e.stopPropagation(); step(-1); });
  inBtn.addEventListener("click", (e) => { e.stopPropagation(); step(1); });
  pct.addEventListener("click", (e) => { e.stopPropagation(); haptic("tap"); tabRecord.zoom = 1; apply(); });
  wrap.appendChild(out);
  wrap.appendChild(pct);
  wrap.appendChild(inBtn);
  apply();
  return wrap;
}

/**
 * Pin button for an open file tab's action bar. Reflects the pin tier via
 * icon (outline → filled → globe) and opens the pin options menu on click,
 * right-click, or long-press — same choices as the file-row context menu.
 */
function mkFvPinBtn(filePath, fileName) {
  const btn = document.createElement("button");
  btn.className = "fv-action-btn fv-pin-btn";
  const sync = () => {
    const tier = pinOpsRef ? pinOpsRef.filePinTier(filePath, fvSessionKey()) : null;
    btn.classList.toggle("active", !!tier);
    // Filled pin = pinned, outline = unpinned. Global pins get the globe ring.
    btn.innerHTML = svgIcon(tier === "global" ? "globe" : tier ? "pinFill" : "pin", 14);
    btn.title = tier ? "Pinned (" + tier + ") — click for pin options" : "Pin options";
  };
  sync();
  // Click (or long-press / right-click) → show the pin options menu, same as
  // the file-row context menu: Pin to session, Pin globally, Unpin.
  const showMenu = (x, y) => {
    if (!pinOpsRef) return;
    const sessionKey = fvSessionKey();
    const tier = pinOpsRef.filePinTier(filePath, sessionKey);
    const actions = [];
    if (sessionKey) {
      actions.push({
        label: tier === "session" ? "Unpin" : "Pin to session",
        icon: svgIcon(tier === "session" ? "pinOff" : "pin", 14),
        onClick: () => { pinOpsRef.toggleFilePin(filePath, fileName, fvSessionKey()); sync(); },
      });
      actions.push({
        label: tier === "global" ? "Unpin globally" : "Pin globally",
        icon: svgIcon("globe", 14),
        onClick: () => { pinOpsRef.toggleFilePinGlobal(filePath, fileName); sync(); },
      });
    } else {
      actions.push({
        label: tier === "global" ? "Unpin globally" : "Pin globally",
        icon: svgIcon(tier === "global" ? "pinOff" : "globe", 14),
        onClick: () => { pinOpsRef.toggleFilePinGlobal(filePath, fileName); sync(); },
      });
    }
    showFvContextMenu(x, y, actions);
  };
  btn.addEventListener("click", (e) => {
    e.stopPropagation(); haptic("tap");
    const r = btn.getBoundingClientRect();
    showMenu(r.left, r.bottom + 4);
  });
  btn.addEventListener("contextmenu", (e) => {
    e.preventDefault(); e.stopPropagation(); haptic("tap");
    showMenu(e.clientX, e.clientY);
  });
  return btn;
}

/** Render a PDF file in an embedded iframe via a blob URL. */
async function loadFvPdf(filePath, fileName, initialView = "file") {
  let blobUrl = null;
  const { fileBody, meta, refreshBtn, tabId } = createFvBinaryTab(filePath, fileName, "📕", () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
  }, initialView);
  const iframe = document.createElement("iframe");
  iframe.className = "fv-pdf";
  fileBody.appendChild(iframe);
  async function loadPdf() {
    if (blobUrl) { URL.revokeObjectURL(blobUrl); blobUrl = null; }
    iframe.src = "";
    meta.textContent = "Loading…";
    try {
      const res = await fetch("/api/raw?path=" + encodeURIComponent(filePath), {
        headers: { "Authorization": "Bearer " + Token.get() },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || res.status);
      }
      const blob = await res.blob();
      blobUrl = URL.createObjectURL(blob);
      iframe.src = blobUrl;
      const metaRes = await fetch("/api/file-meta?path=" + encodeURIComponent(filePath), {
        headers: { "Authorization": "Bearer " + Token.get() },
      }).catch(() => null);
      let mtime = null;
      if (metaRes && metaRes.ok) { const md = await metaRes.json(); mtime = md.mtime; }
      meta.textContent = formatSize(blob.size) + (mtime ? " · " + formatTimeAgo(mtime) : "");
      meta.title = mtime ? new Date(mtime).toLocaleString() : "";
      const t = fvOpenTabs.get(tabId);
      if (t) { t.mtime = mtime; t.size = blob.size; t.stale = false; }
    } catch (e) {
      iframe.remove();
      const errDiv = document.createElement("div");
      errDiv.className = "fv-error";
      errDiv.textContent = "Error: " + (e.message || e);
      fileBody.appendChild(errDiv);
      meta.textContent = "Failed to load";
    }
  }
  refreshBtn.addEventListener("click", () => { haptic("tap"); loadPdf(); });
  await loadPdf();
}

/** Render a DOCX file by converting it to HTML with mammoth.js. */
async function loadFvDocx(filePath, fileName, initialView = "file") {
  const { fileBody, meta, refreshBtn, tabId } = createFvBinaryTab(filePath, fileName, "📘", null, initialView);
  async function loadDocx() {
    fileBody.innerHTML = "";
    meta.textContent = "Loading…";
    try {
      const res = await fetch("/api/raw?path=" + encodeURIComponent(filePath), {
        headers: { "Authorization": "Bearer " + Token.get() },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || res.status);
      }
      const blob = await res.blob();
      const arrayBuffer = await blob.arrayBuffer();
      if (typeof mammoth === "undefined") {
        throw new Error("mammoth.js library failed to load");
      }
      const result = await mammoth.convertToHtml({ arrayBuffer });
      const docDiv = document.createElement("div");
      docDiv.className = "fv-docx";
      docDiv.innerHTML = result.value;
      fileBody.appendChild(docDiv);
      if (result.messages && result.messages.length) {
        const warn = document.createElement("div");
        warn.className = "fv-truncated";
        warn.textContent = result.messages.length + " conversion warning(s)";
        fileBody.appendChild(warn);
      }
      const metaRes = await fetch("/api/file-meta?path=" + encodeURIComponent(filePath), {
        headers: { "Authorization": "Bearer " + Token.get() },
      }).catch(() => null);
      let mtime = null;
      if (metaRes && metaRes.ok) { const md = await metaRes.json(); mtime = md.mtime; }
      meta.textContent = formatSize(blob.size) + (mtime ? " · " + formatTimeAgo(mtime) : "");
      meta.title = mtime ? new Date(mtime).toLocaleString() : "";
      const t = fvOpenTabs.get(tabId);
      if (t) { t.mtime = mtime; t.size = blob.size; t.stale = false; }
    } catch (e) {
      const errDiv = document.createElement("div");
      errDiv.className = "fv-error";
      errDiv.textContent = "Error: " + (e.message || e);
      fileBody.appendChild(errDiv);
      meta.textContent = "Failed to load";
    }
  }
  refreshBtn.addEventListener("click", () => { haptic("tap"); loadDocx(); });
  await loadDocx();
}

/** Parse a CSV string into rows (handles quoted fields). Returns string[][]. */
function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else { inQuotes = false; }
      } else {
        field += c;
      }
    } else {
      if (c === '"') {
        inQuotes = true;
      } else if (c === ",") {
        row.push(field); field = "";
      } else if (c === "\n") {
        row.push(field); rows.push(row); row = []; field = "";
      } else if (c === "\r") {
        // skip — handled by \n
      } else {
        field += c;
      }
    }
  }
  // last field/row
  if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
  return rows;
}

/** Build a <table> element from rows, treating the first row as a header. */
function buildSheetTable(rows) {
  const table = document.createElement("table");
  if (rows.length === 0) return table;
  const thead = document.createElement("thead");
  const headTr = document.createElement("tr");
  for (const cell of rows[0]) {
    const th = document.createElement("th");
    th.textContent = cell;
    headTr.appendChild(th);
  }
  thead.appendChild(headTr);
  table.appendChild(thead);
  const tbody = document.createElement("tbody");
  for (let r = 1; r < rows.length; r++) {
    const tr = document.createElement("tr");
    for (const cell of rows[r]) {
      const td = document.createElement("td");
      td.textContent = cell;
      tr.appendChild(td);
    }
    tbody.appendChild(tr);
  }
  table.appendChild(tbody);
  return table;
}

/** Render a CSV file as a table (fetched as text via /api/file). */
async function loadFvCsv(filePath, fileName, initialView = "file") {
  const { fileBody, meta, refreshBtn, copyBtn, tabId } = createFvBinaryTab(filePath, fileName, "📊", null, initialView);
  let csvContent = null;
  let csvTruncated = false;
  async function loadCsv() {
    fileBody.innerHTML = "";
    meta.textContent = "Loading…";
    try {
      const res = await fetch("/api/file?path=" + encodeURIComponent(filePath), {
        headers: { "Authorization": "Bearer " + Token.get() },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || res.status);
      }
      const data = await res.json();
      csvContent = data.content;
      csvTruncated = !!data.truncated;
      const rows = parseCsv(data.content);
      const wrap = document.createElement("div");
      wrap.className = "fv-sheet";
      const info = document.createElement("div");
      info.className = "fv-sheet-info";
      info.textContent = rows.length + " row(s) · " + (rows[0]?.length || 0) + " column(s)";
      wrap.appendChild(info);
      wrap.appendChild(buildSheetTable(rows));
      fileBody.appendChild(wrap);
      if (data.truncated) {
        const warn = document.createElement("div");
        warn.className = "fv-truncated";
        warn.textContent = "File is " + formatSize(data.size) + " — showing first 1MB only";
        fileBody.appendChild(warn);
      }
      meta.textContent = formatSize(data.size) + (data.truncated ? " · truncated to 1MB" : "") + (data.mtime ? " · " + formatTimeAgo(data.mtime) : "");
      meta.title = data.mtime ? new Date(data.mtime).toLocaleString() : "";
      const t = fvOpenTabs.get(tabId);
      if (t) { t.mtime = data.mtime; t.size = data.size; t.stale = false; }
    } catch (e) {
      const errDiv = document.createElement("div");
      errDiv.className = "fv-error";
      errDiv.textContent = "Error: " + (e.message || e);
      fileBody.appendChild(errDiv);
      meta.textContent = "Failed to load";
    }
  }
  refreshBtn.addEventListener("click", () => { haptic("tap"); loadCsv(); });
  // CSV is text under the hood — copy the raw source, not the table.
  copyBtn.style.display = "";
  copyBtn.addEventListener("click", () => {
    haptic("tap");
    if (csvContent == null) { fvToast("File not loaded yet"); return; }
    void copyText(csvContent).then((ok) => {
      if (!ok) { fvToast("Copy failed"); return; }
      flashCopied(copyBtn);
      if (csvTruncated) fvToast("Copied — file truncated at 1MB");
    });
  });
  await loadCsv();
}

/** Render an XLSX file by parsing it with SheetJS (one table per worksheet). */
async function loadFvXlsx(filePath, fileName, initialView = "file") {
  const { fileBody, meta, refreshBtn, tabId } = createFvBinaryTab(filePath, fileName, "📊", null, initialView);
  async function loadXlsx() {
    fileBody.innerHTML = "";
    meta.textContent = "Loading…";
    try {
      const res = await fetch("/api/raw?path=" + encodeURIComponent(filePath), {
        headers: { "Authorization": "Bearer " + Token.get() },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || res.status);
      }
      const blob = await res.blob();
      const arrayBuffer = await blob.arrayBuffer();
      if (typeof XLSX === "undefined") {
        throw new Error("SheetJS (XLSX) library failed to load");
      }
      const wb = XLSX.read(arrayBuffer, { type: "array" });
      const wrap = document.createElement("div");
      wrap.className = "fv-sheet";
      for (let i = 0; i < wb.SheetNames.length; i++) {
        const name = wb.SheetNames[i];
        const sheet = wb.Sheets[name];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false, defval: "" });
        if (i > 0) {
          const sep = document.createElement("div");
          sep.className = "fv-sheet-sep";
          wrap.appendChild(sep);
        }
        const title = document.createElement("div");
        title.className = "fv-sheet-title";
        title.textContent = name;
        wrap.appendChild(title);
        if (rows.length === 0) {
          const empty = document.createElement("div");
          empty.className = "fv-sheet-info";
          empty.textContent = "Empty sheet";
          wrap.appendChild(empty);
        } else {
          const info = document.createElement("div");
          info.className = "fv-sheet-info";
          info.textContent = rows.length + " row(s) · " + (rows[0]?.length || 0) + " column(s)";
          wrap.appendChild(info);
          wrap.appendChild(buildSheetTable(rows.map((r) => r.map((c) => String(c ?? "")))));
        }
      }
      fileBody.appendChild(wrap);
      const metaRes = await fetch("/api/file-meta?path=" + encodeURIComponent(filePath), {
        headers: { "Authorization": "Bearer " + Token.get() },
      }).catch(() => null);
      let mtime = null;
      if (metaRes && metaRes.ok) { const md = await metaRes.json(); mtime = md.mtime; }
      meta.textContent = formatSize(blob.size) + (mtime ? " · " + formatTimeAgo(mtime) : "");
      meta.title = mtime ? new Date(mtime).toLocaleString() : "";
      const t = fvOpenTabs.get(tabId);
      if (t) { t.mtime = mtime; t.size = blob.size; t.stale = false; }
    } catch (e) {
      const errDiv = document.createElement("div");
      errDiv.className = "fv-error";
      errDiv.textContent = "Error: " + (e.message || e);
      fileBody.appendChild(errDiv);
      meta.textContent = "Failed to load";
    }
  }
  refreshBtn.addEventListener("click", () => { haptic("tap"); loadXlsx(); });
  await loadXlsx();
}

/**
 * Render a PPTX via pptx-preview (esm.sh, lazy-imported on first use — it
 * pulls jszip/echarts/lodash, so it only loads when a deck is actually
 * opened). esm.sh synthesizes named exports for the CJS deps; jsDelivr's
 * +esm doesn't (lodash named imports like `omit` fail there).
 */
let pptxPreviewMod = null;
async function loadFvPptx(filePath, fileName, initialView = "file") {
  const { fileBody, meta, refreshBtn, tabId } = createFvBinaryTab(filePath, fileName, "📽", null, initialView);
  async function loadPptx() {
    fileBody.innerHTML = "";
    meta.textContent = "Loading…";
    try {
      const res = await fetch("/api/raw?path=" + encodeURIComponent(filePath), {
        headers: { "Authorization": "Bearer " + Token.get() },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || res.status);
      }
      const blob = await res.blob();
      const arrayBuffer = await blob.arrayBuffer();
      if (!pptxPreviewMod) {
        // Runtime-only URL (kept in a variable so esbuild leaves the dynamic
        // import alone instead of trying to bundle the CDN module).
        const PPTX_PREVIEW_URL = "https://esm.sh/pptx-preview@1.0.7";
        pptxPreviewMod = await import(/* @vite-ignore */ PPTX_PREVIEW_URL);
      }
      if (!pptxPreviewMod || typeof pptxPreviewMod.init !== "function") {
        throw new Error("pptx-preview library failed to load");
      }
      const wrap = document.createElement("div");
      wrap.className = "fv-pptx";
      fileBody.appendChild(wrap);
      // pptx-preview needs explicit dims — fit to the pane, 16:9.
      const w = Math.max(320, wrap.clientWidth || fileBody.clientWidth || 960);
      const viewer = pptxPreviewMod.init(wrap, { width: w, height: Math.round(w * 9 / 16) });
      await viewer.preview(arrayBuffer);
      const metaRes = await fetch("/api/file-meta?path=" + encodeURIComponent(filePath), {
        headers: { "Authorization": "Bearer " + Token.get() },
      }).catch(() => null);
      let mtime = null;
      if (metaRes && metaRes.ok) { const md = await metaRes.json(); mtime = md.mtime; }
      meta.textContent = formatSize(blob.size) + (mtime ? " · " + formatTimeAgo(mtime) : "");
      meta.title = mtime ? new Date(mtime).toLocaleString() : "";
      const t = fvOpenTabs.get(tabId);
      if (t) { t.mtime = mtime; t.size = blob.size; t.stale = false; }
    } catch (e) {
      const errDiv = document.createElement("div");
      errDiv.className = "fv-error";
      errDiv.textContent = "Error: " + (e.message || e);
      fileBody.appendChild(errDiv);
      meta.textContent = "Failed to load";
    }
  }
  refreshBtn.addEventListener("click", () => { haptic("tap"); loadPptx(); });
  await loadPptx();
}

/** Render an image/audio/video file via a /api/raw blob URL. */
async function loadFvMedia(filePath, fileName, kind, initialView = "file") {
  const icons = { img: "🖼", audio: "🎵", video: "🎬" };
  let blobUrl = null;
  let mediaBlob = null;
  const { fileBody, meta, refreshBtn, copyBtn, tabId } = createFvBinaryTab(filePath, fileName, icons[kind] || "📦", () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
  }, initialView);
  const el = document.createElement(kind === "img" ? "img" : kind);
  el.className = "fv-media fv-media-" + kind;
  if (kind !== "img") el.controls = true;
  fileBody.appendChild(el);
  async function loadMedia() {
    if (blobUrl) { URL.revokeObjectURL(blobUrl); blobUrl = null; }
    el.removeAttribute("src");
    meta.textContent = "Loading…";
    try {
      const res = await fetch("/api/raw?path=" + encodeURIComponent(filePath), {
        headers: { "Authorization": "Bearer " + Token.get() },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || res.status);
      }
      const blob = await res.blob();
      mediaBlob = blob;
      blobUrl = URL.createObjectURL(blob);
      el.src = blobUrl;
      const metaRes = await fetch("/api/file-meta?path=" + encodeURIComponent(filePath), {
        headers: { "Authorization": "Bearer " + Token.get() },
      }).catch(() => null);
      let mtime = null;
      if (metaRes && metaRes.ok) { const md = await metaRes.json(); mtime = md.mtime; }
      meta.textContent = formatSize(blob.size) + (mtime ? " · " + formatTimeAgo(mtime) : "");
      meta.title = mtime ? new Date(mtime).toLocaleString() : "";
      const t = fvOpenTabs.get(tabId);
      if (t) { t.mtime = mtime; t.size = blob.size; t.stale = false; }
    } catch (e) {
      el.remove();
      const errDiv = document.createElement("div");
      errDiv.className = "fv-error";
      errDiv.textContent = "Error: " + (e.message || e);
      fileBody.appendChild(errDiv);
      meta.textContent = "Failed to load";
    }
  }
  refreshBtn.addEventListener("click", () => { haptic("tap"); loadMedia(); });
  // Images can go on the device clipboard via ClipboardItem — browsers only
  // accept image/png, so other formats are transcoded through a canvas
  // (animated GIFs copy as their first frame). Where ClipboardItem is
  // unavailable (Firefox, non-secure contexts) fall back to the file path.
  if (kind === "img") {
    copyBtn.style.display = "";
    copyBtn.addEventListener("click", async () => {
      haptic("tap");
      if (!mediaBlob) { fvToast("Image not loaded yet"); return; }
      try {
        // Pass a promise into ClipboardItem so the transcode doesn't break
        // the user-gesture window Safari requires for clipboard.write.
        const pngPromise = (async () => {
          if (mediaBlob.type === "image/png") return mediaBlob;
          const bmp = await createImageBitmap(mediaBlob);
          const canvas = document.createElement("canvas");
          canvas.width = bmp.width;
          canvas.height = bmp.height;
          canvas.getContext("2d").drawImage(bmp, 0, 0);
          bmp.close();
          return new Promise((res, rej) =>
            canvas.toBlob((b) => (b ? res(b) : rej(new Error("PNG encode failed"))), "image/png"));
        })();
        // Prevent an unhandled rejection if ClipboardItem/clipboard.write
        // fails before the promise is consumed.
        pngPromise.catch(() => {});
        await navigator.clipboard.write([new ClipboardItem({ "image/png": pngPromise })]);
        flashCopied(copyBtn);
      } catch {
        const ok = await copyText(filePath);
        fvToast(ok ? "Image copy unsupported — path copied" : "Copy failed");
      }
    });
  }
  await loadMedia();
}

/**
 * Scroll a text-file <pre> to the given 1-based line range and flash a
 * highlight band. Lines are uniform-height (white-space: pre, no wrap), so
 * offsets are computed from line-height rather than per-line elements.
 */
function scrollPreToLine(pre, startLine, endLine) {
  if (!pre || !startLine) return;
  requestAnimationFrame(() => {
    const cs = getComputedStyle(pre);
    const lh = parseFloat(cs.lineHeight) || 20;
    const pt = parseFloat(cs.paddingTop) || 0;
    const top = pt + (Math.max(1, startLine) - 1) * lh;
    pre.scrollTop = Math.max(0, top - pre.clientHeight * 0.3);
    pre.style.position = "relative";
    const band = document.createElement("div");
    band.className = "fv-line-highlight";
    band.style.top = top + "px";
    band.style.height = lh * Math.max(1, (endLine || startLine) - startLine + 1) + "px";
    pre.appendChild(band);
    setTimeout(() => {
      band.classList.add("fv-line-highlight-fade");
      setTimeout(() => band.remove(), 600);
    }, 1200);
  });
}

/** Scroll an already-open text tab to a line range (tab-reuse path). */
function scrollTabToLine(tab, startLine, endLine) {
  const pre = tab.paneEl.querySelector(".fv-file-body > pre");
  if (pre) scrollPreToLine(pre, startLine, endLine);
}

async function loadFvFile(filePath, fileName, { editMode = false, initialView = "file", initialLine = null, endLine = null } = {}) {
  // Reuse an existing tab for this file if already open
  for (const [id, t] of fvOpenTabs) {
    if (t.filePath === filePath) {
      switchFvTab(id);
      if (t.setView) t.setView(initialView);
      if (initialLine) scrollTabToLine(t, initialLine, endLine);
      return;
    }
  }
  const ext = fileName.split(".").pop()?.toLowerCase() || "";
  // Formats with dedicated renderers (PDF via iframe, DOCX via mammoth.js,
  // XLSX via SheetJS, CSV as a table). Each gets the same File ⇄ Diff toggle.
  if (ext === "pdf") { loadFvPdf(filePath, fileName, initialView); return; }
  if (ext === "docx") { loadFvDocx(filePath, fileName, initialView); return; }
  if (ext === "xlsx") { loadFvXlsx(filePath, fileName, initialView); return; }
  if (ext === "pptx") { loadFvPptx(filePath, fileName, initialView); return; }
  if (ext === "csv") { loadFvCsv(filePath, fileName, initialView); return; }
  const media = mediaKind(ext);
  if (media) { loadFvMedia(filePath, fileName, media, initialView); return; }
  ensureFilesTab();
  const tabId = "file_" + Date.now() + "_" + Math.random().toString(36).slice(2, 6);

  // Tab element
  const tab = document.createElement("div");
  tab.className = "fv-tab";
  tab.dataset.tabId = tabId;
  const label = document.createElement("span");
  label.className = "fv-tab-label";
  label.innerHTML = FV_ICON_FILE + " " + fileName.replace(/</g,"&lt;");
  const closeBtn = document.createElement("button");
  closeBtn.className = "fv-tab-close";
  closeBtn.title = "Close tab";
  closeBtn.textContent = "×";
  closeBtn.addEventListener("click", (e) => { e.stopPropagation(); haptic("tap"); requestCloseFvTab(tabId); });
  tab.appendChild(label);
  tab.appendChild(closeBtn);
  tab.addEventListener("click", () => { haptic("tap"); switchFvTab(tabId); });
  fvTabs.appendChild(tab);

  // Pane element
  const pane = document.createElement("div");
  pane.className = "fv-pane";
  const head = document.createElement("div");
  head.className = "fv-viewer-head";
  const headInfo = document.createElement("div");
  headInfo.className = "fv-head-info";
  const fn = document.createElement("span");
  fn.className = "fv-filename";
  fn.textContent = fileName;
  const nameInput = document.createElement("input");
  nameInput.className = "fv-name-input";
  nameInput.type = "text";
  nameInput.value = fileName;
  nameInput.spellcheck = false;
  nameInput.title = "File name (Save As)";
  nameInput.style.display = "none";
  const meta = document.createElement("span");
  meta.className = "fv-meta";
  meta.textContent = "Loading…";
  headInfo.appendChild(fn);
  headInfo.appendChild(nameInput);
  headInfo.appendChild(meta);
  const actions = document.createElement("span");
  actions.className = "fv-actions";
  const copyBtn = document.createElement("button");
  copyBtn.className = "fv-action-btn fv-copy-btn";
  copyBtn.textContent = "Copy";
  copyBtn.title = "Copy file contents";
  const refreshBtn = document.createElement("button");
  refreshBtn.className = "fv-action-btn fv-refresh-btn";
  refreshBtn.textContent = "↻";
  refreshBtn.title = "Refresh from disk";
  const editBtn = document.createElement("button");
  editBtn.className = "fv-action-btn";
  editBtn.textContent = "Edit";
  editBtn.title = "Edit file";
  const saveBtn = document.createElement("button");
  saveBtn.className = "fv-action-btn fv-save-btn";
  saveBtn.textContent = "Save";
  saveBtn.title = "Save changes";
  saveBtn.style.display = "none";
  const cancelBtn = document.createElement("button");
  cancelBtn.className = "fv-action-btn fv-cancel-btn";
  cancelBtn.textContent = "Cancel";
  cancelBtn.title = "Discard changes";
  cancelBtn.style.display = "none";
  actions.appendChild(mkFvPinBtn(filePath, fileName));
  actions.appendChild(copyBtn);
  actions.appendChild(refreshBtn);
  actions.appendChild(editBtn);
  actions.appendChild(saveBtn);
  actions.appendChild(cancelBtn);
  // File ⇄ Diff toggle in the header. Flipping to Diff lazy-loads the unified
  // diff for this path into diffBody; flipping back restores the file view.
  const fileBody = document.createElement("div");
  fileBody.className = "fv-file-body";
  const diffBody = document.createElement("pre");
  diffBody.className = "fv-diff-pre fv-diff-body hidden";
  let diffLoaded = false;
  const viewToggle = buildFvViewToggle({
    initial: initialView,
    onFile: () => {
      fileBody.classList.remove("hidden");
      diffBody.classList.add("hidden");
      actions.style.display = "";
    },
    onDiff: () => {
      fileBody.classList.add("hidden");
      diffBody.classList.remove("hidden");
      actions.style.display = "none";
      if (!diffLoaded) { diffLoaded = true; loadFileDiffInto(diffBody, filePath); }
    },
  });
  head.appendChild(headInfo);
  head.appendChild(viewToggle.el);
  head.appendChild(actions);
  const pre = document.createElement("pre");
  const code = document.createElement("code");
  pre.appendChild(code);
  fileBody.appendChild(pre);
  pane.appendChild(head);
  pane.appendChild(fileBody);
  pane.appendChild(diffBody);
  fvOverlay.querySelector(".fv-modal").appendChild(pane);

  let originalContent = null;
  let isMarkdown = false;
  let isHtml = false;
  let fileTruncated = false;
  let textarea = null;
  let viewEl = pre;

  // Dirty = the editor is on screen with content differing from disk.
  // Closing the tab checks this via requestCloseFvTab; the tab gets a ● dot.
  const isDirty = () => !!(textarea && textarea.parentNode && textarea.value !== originalContent);

  const tabRecord = { tabEl: tab, paneEl: pane, fileName, filePath, setView: viewToggle.setView, mtime: null, size: null, icon: FV_ICON_FILE, isDirty, zoom: 1 };
  fvOpenTabs.set(tabId, tabRecord);
  actions.appendChild(mkFvZoomCtrls(tabRecord, [fileBody, diffBody]));
  switchFvTab(tabId);
  const dirPath = filePath.includes("/") ? filePath.slice(0, filePath.lastIndexOf("/")) : "";

  /** Build a sandboxed iframe that renders the given HTML source via srcdoc. */
  function buildHtmlIframe(html) {
    const iframe = document.createElement("iframe");
    iframe.className = "fv-html";
    iframe.sandbox = "allow-scripts allow-popups allow-forms allow-modals";
    iframe.srcdoc = html;
    return iframe;
  }

  /** Render file content (text/markdown/html) into the view area. */
  function renderContent(content, ext) {
    if (ext === "md" || ext === "markdown") {
      isMarkdown = true; isHtml = false;
      const html = marked.parse(content, { breaks: true, gfm: true });
      const mdDiv = document.createElement("div");
      mdDiv.className = "fv-markdown";
      mdDiv.innerHTML = html;
      viewEl.replaceWith(mdDiv);
      viewEl = mdDiv;
      mdDiv.querySelectorAll("pre code").forEach((block) => {
        try { hljs.highlightElement(block); } catch {}
      });
    } else if (ext === "html" || ext === "htm") {
      isHtml = true; isMarkdown = false;
      const iframe = buildHtmlIframe(content);
      viewEl.replaceWith(iframe);
      viewEl = iframe;
    } else {
      isMarkdown = false; isHtml = false;
      code.textContent = content;
      code.className = "hljs";
      let highlighted = null;
      if (ext && hljs.getLanguage(ext)) {
        try { highlighted = hljs.highlight(content, { language: ext }).value; } catch {}
      }
      if (!highlighted) {
        try { highlighted = hljs.highlightAuto(content).value; } catch {}
      }
      if (highlighted) {
        code.innerHTML = highlighted;
        if (ext && hljs.getLanguage(ext)) code.classList.add("language-" + ext);
      }
    }
  }

  /** Update meta text with size + last-modified time. */
  function updateMeta(size, mtime, truncated) {
    const parts = [formatSize(size)];
    if (truncated) parts.push("truncated to 1MB");
    const ago = formatTimeAgo(mtime);
    if (ago) parts.push(ago);
    meta.textContent = parts.join(" · ");
    meta.title = mtime ? new Date(mtime).toLocaleString() : "";
  }

  /** Re-fetch the file from disk and re-render. */
  async function refreshFromDisk() {
    haptic("tap");
    if (textarea && textarea.parentNode) {
      if (!confirm("Discard unsaved changes and reload from disk?")) return;
      setEditing(false);
    }
    refreshBtn.disabled = true;
    meta.textContent = "Refreshing…";
    try {
      const res = await fetch("/api/file?path=" + encodeURIComponent(filePath), {
        headers: { "Authorization": "Bearer " + Token.get() },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || res.status);
      }
      const data = await res.json();
      originalContent = data.content;
      fileTruncated = !!data.truncated;
      fn.textContent = data.name;
      updateMeta(data.size, data.mtime, data.truncated);
      editBtn.style.display = data.truncated ? "none" : "";
      editBtn.title = data.truncated ? "Editing disabled — file is truncated" : "Edit file";
      renderContent(data.content, data.ext);
      // Update tab metadata for freshness checks
      const t = fvOpenTabs.get(tabId);
      if (t) { t.mtime = data.mtime; t.size = data.size; t.stale = false; tab.classList.remove("fv-tab-stale"); }
      // Remove any existing truncation warning
      const warn = fileBody.querySelector(".fv-truncated");
      if (warn) warn.remove();
      if (data.truncated) {
        const w = document.createElement("div");
        w.className = "fv-truncated";
        w.textContent = "File is " + formatSize(data.size) + " — showing first 1MB only";
        fileBody.appendChild(w);
      }
    } catch (e) {
      meta.textContent = "Refresh failed";
    } finally {
      refreshBtn.disabled = false;
    }
  }

  refreshBtn.addEventListener("click", refreshFromDisk);

  // Copy the file's contents to the device clipboard — the edit buffer when
  // editing (so unsaved changes are included), else the loaded source.
  copyBtn.addEventListener("click", () => {
    haptic("tap");
    const editing = !!(textarea && textarea.parentNode);
    const text = editing ? textarea.value : originalContent;
    if (text == null) { fvToast("File not loaded yet"); return; }
    void copyText(text).then((ok) => {
      if (!ok) { fvToast("Copy failed"); return; }
      flashCopied(copyBtn);
      if (fileTruncated && !editing) fvToast("Copied — file truncated at 1MB");
    });
  });

  function setEditing(editing) {
    editBtn.style.display = editing ? "none" : "";
    saveBtn.style.display = editing ? "" : "none";
    cancelBtn.style.display = editing ? "" : "none";
    fn.style.display = editing ? "none" : "";
    nameInput.style.display = editing ? "" : "none";
    if (editing) {
      if (!textarea) {
        textarea = document.createElement("textarea");
        textarea.className = "fv-editor";
        textarea.value = originalContent ?? "";
        textarea.spellcheck = false;
        textarea.addEventListener("input", () => {
          tab.classList.toggle("fv-tab-dirty", isDirty());
        });
        textarea.addEventListener("keydown", (e) => {
          if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
            e.preventDefault();
            saveFile();
          }
        });
      }
      if (viewEl.parentNode) viewEl.replaceWith(textarea);
      textarea.focus();
    } else {
      if (textarea && textarea.parentNode) textarea.replaceWith(viewEl);
      tab.classList.remove("fv-tab-dirty");
    }
  }

  async function saveFile() {
    if (!textarea) return;
    const content = textarea.value;
    const newName = nameInput.value.trim() || fileName;
    // Reject path separators in the name — keep it within the original directory.
    if (/[\\/]/.test(newName)) {
      alert("File name may not contain path separators.");
      nameInput.focus();
      return;
    }
    const targetPath = dirPath ? dirPath + "/" + newName : newName;
    saveBtn.disabled = true;
    saveBtn.textContent = "Saving…";
    meta.textContent = "Saving…";
    try {
      const res = await fetch("/api/file", {
        method: "POST",
        headers: {
          "Authorization": "Bearer " + Token.get(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ path: targetPath, content }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || res.status);
      }
      const data = await res.json();
      originalContent = content;
      fn.textContent = data.name || newName;
      nameInput.value = data.name || newName;
      updateMeta(data.size, data.mtime, false);
      setEditing(false);
      tab.classList.remove("fv-tab-dirty");
      // Re-render the (now saved) read view
      const ext = (data.name || newName).split(".").pop()?.toLowerCase() || "";
      renderContent(content, ext);
      // Update tab metadata
      const t = fvOpenTabs.get(tabId);
      if (t) { t.mtime = data.mtime; t.size = data.size; }
    } catch (e) {
      meta.textContent = "Save failed";
      alert("Save failed: " + (e.message || e));
    } finally {
      saveBtn.disabled = false;
      saveBtn.textContent = "Save";
    }
  }

  editBtn.addEventListener("click", () => {
    haptic("tap");
    if (originalContent === null) return;
    if (isMarkdown) {
      if (!confirm("Edit this markdown file as raw text?")) return;
    }
    if (isHtml) {
      if (!confirm("Edit this HTML file as raw text?")) return;
    }
    setEditing(true);
  });
  saveBtn.addEventListener("click", () => { haptic("tap"); saveFile(); });
  cancelBtn.addEventListener("click", async () => {
    haptic("tap");
    if (isDirty()) {
      const ok = await customConfirm({
        title: "Discard changes?",
        message: "You have unsaved edits in this file.",
        confirmText: "Discard",
        destructive: true,
      });
      if (!ok) return;
      textarea.value = originalContent ?? "";
    }
    setEditing(false);
  });
  nameInput.addEventListener("keydown", (e) => {
    if (e.key === "Enter") { e.preventDefault(); saveBtn.click(); }
  });

  try {
    const res = await fetch("/api/file?path=" + encodeURIComponent(filePath), {
      headers: { "Authorization": "Bearer " + Token.get() },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      code.textContent = "Error: " + (err.error || res.status);
      meta.textContent = "";
      editBtn.style.display = "none";
      return;
    }
    const data = await res.json();
    fn.textContent = data.name;
    originalContent = data.content;
    fileTruncated = !!data.truncated;
    updateMeta(data.size, data.mtime, data.truncated);
    if (data.truncated) {
      editBtn.style.display = "none";
      editBtn.title = "Editing disabled — file is truncated";
    }
    renderContent(data.content, data.ext);
    // Jump to a requested line range (from file-link ?lines=a-b). Only
    // meaningful for the plain-text view — markdown/HTML don't map to
    // source lines.
    if (initialLine && !isMarkdown && !isHtml) scrollPreToLine(pre, initialLine, endLine);
    // Store mtime/size on tab for freshness checks
    const t0 = fvOpenTabs.get(tabId);
    if (t0) { t0.mtime = data.mtime; t0.size = data.size; }
    if (data.truncated) {
      const warn = document.createElement("div");
      warn.className = "fv-truncated";
      warn.textContent = "File is " + formatSize(data.size) + " — showing first 1MB only";
      fileBody.appendChild(warn);
    }
    // For newly-created (empty) files, jump straight into edit mode.
    if (editMode && !data.truncated) setEditing(true);
  } catch (e) {
    code.textContent = "Error: " + (e.message || e);
    meta.textContent = "";
    editBtn.style.display = "none";
  }
}

sbFilesBtn.addEventListener("click", () => { haptic("tap"); openFileViewer(); });
fvClose.addEventListener("click", () => { haptic("tap"); closeFileViewer(); });
fvOverlay.addEventListener("click", (e) => { if (e.target === fvOverlay) { haptic("tap"); closeFileViewer(); } });
fvUp.addEventListener("click", () => { haptic("tap"); if (fvCurrentPath) { const sep = fvCurrentPath.includes("\\") ? "\\" : "/"; loadFvDir(fvCurrentPath + sep + ".."); } });
fvHome.addEventListener("click", () => { haptic("tap"); if (fvHomeDir) loadFvDir(fvHomeDir); });
fvGo.addEventListener("click", () => { haptic("tap"); if (fvPathInput.value.trim()) loadFvDir(fvPathInput.value.trim()); });
fvPathInput.addEventListener("keydown", (e) => { if (e.key === "Enter") fvGo.click(); });

// Sort control: changing the key or direction re-sorts the cached listing
// (client-side, no refetch).
if (fvSortKey) {
  fvSortKey.value = fvSort.key;
  fvSortKey.addEventListener("change", () => {
    haptic("tap");
    fvSort.key = fvSortKey.value;
    saveFvSort();
    renderFvList();
  });
}
if (fvSortDir) {
  const syncSortDir = () => { fvSortDir.textContent = fvSort.dir === "asc" ? "▲" : "▼"; };
  syncSortDir();
  fvSortDir.addEventListener("click", () => {
    haptic("tap");
    fvSort.dir = fvSort.dir === "asc" ? "desc" : "asc";
    syncSortDir();
    saveFvSort();
    renderFvList();
  });
}

// Filter box: client-side substring match on the current folder's entries.
if (fvFilter) {
  fvFilter.addEventListener("input", () => renderFvList());
}

// Show-hidden toggle: re-fetch the current folder with ?hidden=1.
if (fvHidden) {
  const syncHidden = () => {
    fvHidden.classList.toggle("active", fvShowHidden);
    fvHidden.title = fvShowHidden ? "Hide hidden files" : "Show hidden files";
  };
  syncHidden();
  fvHidden.addEventListener("click", () => {
    haptic("tap");
    fvShowHidden = !fvShowHidden;
    try { localStorage.setItem("fvShowHidden", fvShowHidden ? "1" : "0"); } catch { /* ignore */ }
    syncHidden();
    if (fvCurrentPath) loadFvDir(fvCurrentPath);
  });
}

/**
 * Custom prompt dialog (works in PWA standalone mode where window.prompt is disabled).
 * Returns a Promise that resolves to the entered string (trimmed) or null if cancelled.
 */
function customPrompt({ title, label, placeholder = "", defaultValue = "", confirmText = "OK", validate }) {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "cp-overlay";
    const modal = document.createElement("div");
    modal.className = "cp-modal";
    const head = document.createElement("div");
    head.className = "cp-head";
    const h2 = document.createElement("h2");
    h2.textContent = title;
    const closeBtn = document.createElement("button");
    closeBtn.className = "cp-close";
    closeBtn.textContent = "×";
    closeBtn.title = "Cancel";
    head.appendChild(h2);
    head.appendChild(closeBtn);
    const body = document.createElement("div");
    body.className = "cp-body";
    if (label) {
      const lab = document.createElement("label");
      lab.className = "cp-label";
      lab.textContent = label;
      lab.htmlFor = "cp-input-" + Date.now();
      body.appendChild(lab);
    }
    const inputEl = document.createElement("input");
    inputEl.className = "cp-input";
    inputEl.id = "cp-input-" + Date.now();
    inputEl.type = "text";
    inputEl.value = defaultValue;
    inputEl.placeholder = placeholder;
    inputEl.spellcheck = false;
    inputEl.autocomplete = "off";
    body.appendChild(inputEl);
    const errEl = document.createElement("div");
    errEl.className = "cp-error";
    errEl.hidden = true;
    body.appendChild(errEl);
    const actions = document.createElement("div");
    actions.className = "cp-actions";
    const cancelBtn = document.createElement("button");
    cancelBtn.className = "cp-btn cp-cancel";
    cancelBtn.textContent = "Cancel";
    const okBtn = document.createElement("button");
    okBtn.className = "cp-btn cp-ok";
    okBtn.textContent = confirmText;
    actions.appendChild(cancelBtn);
    actions.appendChild(okBtn);
    modal.appendChild(head);
    modal.appendChild(body);
    modal.appendChild(actions);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    let done = false;
    function finish(value) {
      if (done) return;
      done = true;
      overlay.remove();
      resolve(value);
    }
    function cancel() { haptic("tap"); finish(null); }
    function confirm() {
      haptic("tap");
      const val = inputEl.value;
      if (validate) {
        const err = validate(val);
        if (err) {
          errEl.textContent = err;
          errEl.hidden = false;
          inputEl.focus();
          inputEl.select();
          return;
        }
      }
      finish(val.trim());
    }
    closeBtn.addEventListener("click", cancel);
    cancelBtn.addEventListener("click", cancel);
    okBtn.addEventListener("click", confirm);
    inputEl.addEventListener("keydown", (e) => {
      if (e.key === "Enter") { e.preventDefault(); confirm(); }
      else if (e.key === "Escape") { e.preventDefault(); cancel(); }
      else { errEl.hidden = true; }
    });
    overlay.addEventListener("click", (e) => { if (e.target === overlay) cancel(); });
    // Focus + select after the element is in the DOM
    requestAnimationFrame(() => { inputEl.focus(); inputEl.select(); });
  });
}

/**
 * Custom confirm dialog (works in PWA standalone mode where window.confirm is disabled).
 * Returns a Promise that resolves to true if confirmed, false if cancelled.
 * Set destructive: true to style the confirm button as a destructive (red) action.
 */
function customConfirm({ title, message = "", confirmText = "Confirm", cancelText = "Cancel", destructive = false }) {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "cp-overlay";
    const modal = document.createElement("div");
    modal.className = "cp-modal";
    const head = document.createElement("div");
    head.className = "cp-head";
    const h2 = document.createElement("h2");
    h2.textContent = title;
    const closeBtn = document.createElement("button");
    closeBtn.className = "cp-close";
    closeBtn.textContent = "×";
    closeBtn.title = "Cancel";
    head.appendChild(h2);
    head.appendChild(closeBtn);
    const body = document.createElement("div");
    body.className = "cp-body";
    if (message) {
      const msg = document.createElement("div");
      msg.className = "cp-message";
      msg.textContent = message;
      body.appendChild(msg);
    }
    const actions = document.createElement("div");
    actions.className = "cp-actions";
    const cancelBtn = document.createElement("button");
    cancelBtn.className = "cp-btn cp-cancel";
    cancelBtn.textContent = cancelText;
    const okBtn = document.createElement("button");
    okBtn.className = "cp-btn cp-ok" + (destructive ? " cp-destructive" : "");
    okBtn.textContent = confirmText;
    actions.appendChild(cancelBtn);
    actions.appendChild(okBtn);
    modal.appendChild(head);
    modal.appendChild(body);
    modal.appendChild(actions);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    let done = false;
    function finish(value) {
      if (done) return;
      done = true;
      document.removeEventListener("keydown", keyHandler);
      overlay.remove();
      resolve(value);
    }
    function cancel() { haptic("tap"); finish(false); }
    function confirm() { haptic("tap"); finish(true); }
    function keyHandler(e) {
      if (e.key === "Escape") { e.preventDefault(); cancel(); }
      else if (e.key === "Enter") { e.preventDefault(); confirm(); }
    }
    closeBtn.addEventListener("click", cancel);
    cancelBtn.addEventListener("click", cancel);
    okBtn.addEventListener("click", confirm);
    overlay.addEventListener("click", (e) => { if (e.target === overlay) cancel(); });
    document.addEventListener("keydown", keyHandler);
    requestAnimationFrame(() => { okBtn.focus(); });
  });
}

// "New" button — opens a dialog with a File ⇄ Folder toggle and a single name
// input. Creating a file opens it in the editor; creating a folder refreshes
// the listing and navigates into it.
fvNewFile.addEventListener("click", async () => {
  haptic("tap");
  if (!fvCurrentPath) return;
  const result = await customNewDialog({ dirPath: fvCurrentPath });
  if (!result) return;
  const sep = fvCurrentPath.includes("\\") ? "\\" : "/";
  const fullPath = fvCurrentPath + sep + result.name;
  if (result.kind === "folder") {
    try {
      const res = await fetch("/api/folder", {
        method: "POST",
        headers: {
          "Authorization": "Bearer " + Token.get(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ path: fullPath }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || res.status);
      }
      await loadFvDir(fullPath);
    } catch (e) {
      alert("Create folder failed: " + (e.message || e));
    }
  } else {
    try {
      const res = await fetch("/api/file", {
        method: "POST",
        headers: {
          "Authorization": "Bearer " + Token.get(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ path: fullPath, content: "" }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || res.status);
      }
      // Refresh the folder listing, then open the new file in the editor
      // (in edit mode, since it's a brand-new empty file).
      await loadFvDir(fvCurrentPath);
      loadFvFile(fullPath, result.name, { editMode: true });
    } catch (e) {
      alert("Create failed: " + (e.message || e));
    }
  }
});

/**
 * New-item dialog: a File ⇄ Folder segmented toggle plus a single name input.
 * Returns { kind: "file"|"folder", name } or null if cancelled. Reuses the
 * cp-modal styling so it matches the existing prompt/confirm dialogs.
 */
function customNewDialog({ dirPath }) {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "cp-overlay";
    const modal = document.createElement("div");
    modal.className = "cp-modal";
    const head = document.createElement("div");
    head.className = "cp-head";
    const h2 = document.createElement("h2");
    h2.textContent = "New";
    const closeBtn = document.createElement("button");
    closeBtn.className = "cp-close";
    closeBtn.textContent = "×";
    closeBtn.title = "Cancel";
    head.appendChild(h2);
    head.appendChild(closeBtn);
    const body = document.createElement("div");
    body.className = "cp-body";

    // Segmented File ⇄ Folder toggle.
    const toggle = document.createElement("div");
    toggle.className = "cp-segmented";
    const fileBtn = document.createElement("button");
    fileBtn.type = "button";
    fileBtn.className = "cp-seg-btn active";
    fileBtn.innerHTML = svgIcon("file", 14) + " File";
    const folderBtn = document.createElement("button");
    folderBtn.type = "button";
    folderBtn.className = "cp-seg-btn";
    folderBtn.innerHTML = svgIcon("folder", 14) + " Folder";
    toggle.appendChild(fileBtn);
    toggle.appendChild(folderBtn);
    body.appendChild(toggle);

    const label = document.createElement("label");
    label.className = "cp-label";
    label.textContent = "File name";
    body.appendChild(label);
    const inputEl = document.createElement("input");
    inputEl.className = "cp-input";
    inputEl.type = "text";
    inputEl.placeholder = "example.txt";
    inputEl.spellcheck = false;
    inputEl.autocomplete = "off";
    body.appendChild(inputEl);
    const errEl = document.createElement("div");
    errEl.className = "cp-error";
    errEl.hidden = true;
    body.appendChild(errEl);
    const actions = document.createElement("div");
    actions.className = "cp-actions";
    const cancelBtn = document.createElement("button");
    cancelBtn.className = "cp-btn cp-cancel";
    cancelBtn.textContent = "Cancel";
    const okBtn = document.createElement("button");
    okBtn.className = "cp-btn cp-ok";
    okBtn.textContent = "Create";
    actions.appendChild(cancelBtn);
    actions.appendChild(okBtn);
    modal.appendChild(head);
    modal.appendChild(body);
    modal.appendChild(actions);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    let kind = "file";
    function setKind(k) {
      if (k === kind) return;
      kind = k;
      fileBtn.classList.toggle("active", k === "file");
      folderBtn.classList.toggle("active", k === "folder");
      label.textContent = k === "file" ? "File name" : "Folder name";
      inputEl.placeholder = k === "file" ? "example.txt" : "new-folder";
      errEl.hidden = true;
    }
    fileBtn.addEventListener("click", () => { haptic("tap"); setKind("file"); inputEl.focus(); });
    folderBtn.addEventListener("click", () => { haptic("tap"); setKind("folder"); inputEl.focus(); });

    let done = false;
    function finish(value) {
      if (done) return;
      done = true;
      overlay.remove();
      resolve(value);
    }
    function cancel() { haptic("tap"); finish(null); }
    function confirm() {
      haptic("tap");
      const val = inputEl.value.trim();
      if (!val) {
        errEl.textContent = (kind === "file" ? "File" : "Folder") + " name is required.";
        errEl.hidden = false;
        inputEl.focus();
        inputEl.select();
        return;
      }
      if (val.includes("/") || val.includes("\\") || val === "." || val === "..") {
        errEl.textContent = "Invalid name.";
        errEl.hidden = false;
        inputEl.focus();
        inputEl.select();
        return;
      }
      finish({ kind, name: val });
    }
    closeBtn.addEventListener("click", cancel);
    cancelBtn.addEventListener("click", cancel);
    okBtn.addEventListener("click", confirm);
    inputEl.addEventListener("keydown", (e) => {
      if (e.key === "Enter") { e.preventDefault(); confirm(); }
      else if (e.key === "Escape") { e.preventDefault(); cancel(); }
      else { errEl.hidden = true; }
    });
    overlay.addEventListener("click", (e) => { if (e.target === overlay) cancel(); });
    requestAnimationFrame(() => { inputEl.focus(); inputEl.select(); });
  });
}

// Upload button — pick local files and upload them into the current folder.
fvUpload.addEventListener("click", () => {
  haptic("tap");
  if (!fvCurrentPath) return;
  fvUploadInput.value = "";
  fvUploadInput.click();
});

fvUploadInput.addEventListener("change", async () => {
  const files = Array.from(fvUploadInput.files || []);
  if (!files.length || !fvCurrentPath) return;
  await uploadFvFiles(fvCurrentPath, files);
});

/**
 * Upload an array of Files into the given directory via multipart/form-data.
 * Shows a small progress indicator while uploading, then refreshes the folder.
 */
async function uploadFvFiles(dir, files) {
  const overlay = document.createElement("div");
  overlay.className = "fv-upload-overlay";
  const box = document.createElement("div");
  box.className = "fv-upload-box";
  const title = document.createElement("div");
  title.className = "fv-upload-title";
  title.textContent = "Uploading " + files.length + " file" + (files.length > 1 ? "s" : "") + "…";
  const bar = document.createElement("div");
  bar.className = "fv-upload-bar";
  const fill = document.createElement("div");
  fill.className = "fv-upload-fill";
  bar.appendChild(fill);
  const status = document.createElement("div");
  status.className = "fv-upload-status";
  status.textContent = "0 / " + files.length;
  box.appendChild(title);
  box.appendChild(bar);
  box.appendChild(status);
  overlay.appendChild(box);
  document.body.appendChild(overlay);

  let done = 0;
  let failed = 0;
  const errors = [];
  for (const file of files) {
    try {
      const form = new FormData();
      form.append("file", file, file.name);
      const res = await fetch("/api/upload?dir=" + encodeURIComponent(dir), {
        method: "POST",
        headers: { "Authorization": "Bearer " + Token.get() },
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || res.status);
      }
    } catch (e) {
      failed++;
      errors.push(file.name + ": " + (e.message || e));
    }
    done++;
    fill.style.width = Math.round((done / files.length) * 100) + "%";
    status.textContent = done + " / " + files.length + (failed ? "  (" + failed + " failed)" : "");
  }
  overlay.remove();
  if (failed) {
    alert("Upload completed with " + failed + " failure(s):\n\n" + errors.join("\n"));
  }
  await loadFvDir(dir);
}

// ---- image lightbox: click any image to view it at natural aspect ratio ----
// Covers chat bubbles (msg-image), tool cards (tc-image), the file viewer
// (media tabs, docx/markdown bodies), widgets, and the pin peek.
let lightboxWired = false;
function initImageZoom() {
  if (lightboxWired) return;
  lightboxWired = true;
  document.addEventListener("click", (e) => {
    const img = e.target.closest && e.target.closest(
      "img.msg-image, img.tc-image, img.fv-media-img, .fv-docx img, .fv-markdown img, .w-markdown img, .pin-peek-md img"
    );
    if (!img || !img.src) return;
    openImageLightbox(img.src);
  });
}

function openImageLightbox(src) {
  const ov = document.createElement("div");
  ov.className = "img-lightbox";
  const img = document.createElement("img");
  img.src = src;
  ov.appendChild(img);
  document.body.appendChild(ov);
  const key = (e) => { if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); close(); } };
  function close() { ov.remove(); document.removeEventListener("keydown", key, true); }
  ov.addEventListener("click", () => { haptic("tap"); close(); });
  // Capture phase so Esc closes the lightbox before the fv/overlay chain.
  document.addEventListener("keydown", key, true);
}

// Pick-intent footer wiring.
fvPickConfirm.addEventListener("click", confirmPick);
fvPickCancel.addEventListener("click", () => { haptic("tap"); closeFileViewer(); });

/** Open a specific file in the viewer (browse intent) — used by the pin
 *  strip's "Open in Files" and file-link clicks. */
function openFileInViewer(filePath, fileName, opts = {}) {
  // Force browse intent — clears any leftover pick chrome if the viewer was
  // last used to pick a working directory.
  fvIntent = "browse";
  fvOverlay.classList.remove("fv-pick-mode");
  fvPickRecents.hidden = true;
  fvPickFoot.hidden = true;
  fvOverlay.hidden = false;
  closeSidebarRef();
  ensureFilesTab();
  // Load the file's parent folder into the Files tab so "Files" always shows
  // the directory the opened file lives in (previously the pane was empty
  // when fv was opened directly to a file, e.g. from a pin or link).
  const sep = filePath.includes("\\") ? "\\" : "/";
  const parent = filePath.slice(0, filePath.lastIndexOf(sep)) || sep;
  if (fvCurrentPath !== parent) loadFvDir(parent);
  loadFvFile(filePath, fileName, opts);
}

/** True while the viewer is open in pick intent (recents refresh target). */
function isFvPickerOpen() {
  return !fvOverlay.hidden && fvIntent === "pick";
}

// ============================================================================
// Exports — functions called from main.js
// ============================================================================

/**
 * Initialize the file viewer module with dependencies from main.js.
 * @param {object} deps
 */
export function initFileViewer(deps) {
  activeCwdRef = deps.activeCwd;
  closeSidebarRef = deps.closeSidebar;
  selectedDirRef = deps.selectedDir || selectedDirRef;
  setSelectedDirRef = deps.setSelectedDir || setSelectedDirRef;
  startSessionRef = deps.startSession || startSessionRef;
  changeFolderRef = deps.changeFolder || changeFolderRef;
  liveCwdsRef = deps.liveCwds || liveCwdsRef;
  removeRecentDirRef = deps.removeRecentDir || removeRecentDirRef;
  forSessionKeyRef = deps.forSessionKey || forSessionKeyRef;
  pinOpsRef = deps.pinOps || null;
}

export { wireFileLinks, openFileViewer, openFileInViewer, closeFileViewer, loadFvDir, restoreFvState, flushFvStateForCwdChange, closeFvTab, isFvPickerOpen, renderFvPickRecents, fvToast, initImageZoom };
